import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Tooltip } from "./common/Tooltip";
import { useSearchParams } from "react-router-dom";
import { useRTLDirection } from "@/hooks/useRTLDirection";
import Markdown from "react-markdown";
import { PrismAsyncLight as SyntaxHighlighter } from "react-syntax-highlighter";
import remarkGfm from "remark-gfm";
import rehypeExternalLinks from "rehype-external-links";
import rehypeRaw from "rehype-raw";
import Sources from "./Sources";
import { motion, AnimatePresence } from "framer-motion";
import { PopupButton } from "react-calendly";
import {
  convertBrCbbToLineBreaks,
  extractClickableOptionsData,
  extractEcommerceData,
  extractMediaLinks,
  extractSeperationData,
  removeClickableOptions,
  removeEcommerceContent,
  renderHTML,
  sanitizeCustomTags,
  isPaymentButton,
  isPaymentButtonComplete,
  removePaymentButton,
  parsePaymentButton,
  checkOtpCollection,
} from "@/utils/renderUtility";
import getTextColor from "@/utils/getTextColor";
import { axiosPost } from "@/services/axiosService";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import ProductCard, { EcommerceProduct } from "./ProductCard";
import { PaymentButton } from "./PaymentButton";
import { OTPCollection } from "./OTPCollection";

import bash from "react-syntax-highlighter/dist/esm/languages/prism/bash";
import c from "react-syntax-highlighter/dist/esm/languages/prism/c";
import cpp from "react-syntax-highlighter/dist/esm/languages/prism/cpp";
import csharp from "react-syntax-highlighter/dist/esm/languages/prism/csharp";
import css from "react-syntax-highlighter/dist/esm/languages/prism/css";
import docker from "react-syntax-highlighter/dist/esm/languages/prism/docker";
import go from "react-syntax-highlighter/dist/esm/languages/prism/go";
import java from "react-syntax-highlighter/dist/esm/languages/prism/java";
import javascript from "react-syntax-highlighter/dist/esm/languages/prism/javascript";
import json from "react-syntax-highlighter/dist/esm/languages/prism/json";
import kotlin from "react-syntax-highlighter/dist/esm/languages/prism/kotlin";
import latex from "react-syntax-highlighter/dist/esm/languages/prism/latex";
import lua from "react-syntax-highlighter/dist/esm/languages/prism/lua";
import makefile from "react-syntax-highlighter/dist/esm/languages/prism/makefile";
import markdown from "react-syntax-highlighter/dist/esm/languages/prism/markdown";
import python from "react-syntax-highlighter/dist/esm/languages/prism/python";
import rust from "react-syntax-highlighter/dist/esm/languages/prism/rust";
import swift from "react-syntax-highlighter/dist/esm/languages/prism/swift";
import typescript from "react-syntax-highlighter/dist/esm/languages/prism/typescript";
import xmlDoc from "react-syntax-highlighter/dist/esm/languages/prism/xml-doc";
import yaml from "react-syntax-highlighter/dist/esm/languages/prism/yaml";
import jsx from "react-syntax-highlighter/dist/esm/languages/prism/jsx";
import deriveShade from "@/utils/deriveShade";
import ReasoningDisplay from "./ReasoningDisplay";
import {
  isOnlyEmoji,
  processChildrenWithEmojis,
  wrapEmojisInHTML,
} from "@/hooks/useEmojiWrapper";
import getColorWithAlpha from "@/utils/getColorWithAlpha";

SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("c", c);
SyntaxHighlighter.registerLanguage("cpp", cpp);
SyntaxHighlighter.registerLanguage("csharp", csharp);
SyntaxHighlighter.registerLanguage("css", css);
SyntaxHighlighter.registerLanguage("docker", docker);
SyntaxHighlighter.registerLanguage("go", go);
SyntaxHighlighter.registerLanguage("java", java);
SyntaxHighlighter.registerLanguage("javascript", javascript);
SyntaxHighlighter.registerLanguage("json", json);
SyntaxHighlighter.registerLanguage("kotlin", kotlin);
SyntaxHighlighter.registerLanguage("latex", latex);
SyntaxHighlighter.registerLanguage("lua", lua);
SyntaxHighlighter.registerLanguage("makefile", makefile);
SyntaxHighlighter.registerLanguage("markdown", markdown);
SyntaxHighlighter.registerLanguage("python", python);
SyntaxHighlighter.registerLanguage("rust", rust);
SyntaxHighlighter.registerLanguage("swift", swift);
SyntaxHighlighter.registerLanguage("typescript", typescript);
SyntaxHighlighter.registerLanguage("xml-doc", xmlDoc);
SyntaxHighlighter.registerLanguage("yaml", yaml);
SyntaxHighlighter.registerLanguage("jsx", jsx);

export const BotComponent = forwardRef(function (props: any, ref) {
  const {
    currentIndex,
    chatConfig,
    msg,
    botData,
    starterQues,
    activeCat,
    handleStarterQues,
    textColor,
    embed,
    suggestedQuesAns,
    message,
    setReadPdf,
    setReadPdfText,
    dataSource,
    handleReaction,
    userCredential,
    calendlyConfig,
    handleUserReply,
    isAnswerSaved,
    handleRegenerate,
    embedConfig,
    responseLoader,
    toolLoader,
    previousChat,
    responseCacheOptions,
    hideClickableOptions,
    customGoalsMsg,
    embedView,
    reasoning,
    steps,
    setDisableInput,
    isLast,
    isOverLimit,
  } = props;

  const rtl = useRTLDirection(chatConfig);
  const typography = chatConfig?.typography;
  const preMadeBotAvatars = [
    "https://dlvkyia8i4zmz.cloudfront.net/Sbo9LjGyTG2xV5b3QbpD_Group_1618874894.svg",
    "https://dlvkyia8i4zmz.cloudfront.net/XtPbs5FyRQaU9OC3MUpj_Group_1618874895.svg",
    "https://dlvkyia8i4zmz.cloudfront.net/zAGWWClxS3KPCQ0kxYda_Group_1618874896.svg",
    "https://dlvkyia8i4zmz.cloudfront.net/gkMCaB2tR6Clx3Valpfr_Group_1618874897.svg",
  ];
  const [searchParams, _] = useSearchParams();
  const isPreviewMode = window.location.pathname.includes("/preview/");
  const isDebugging = useMemo(
    () => (searchParams.get("debug") === "true" ? true : false),
    [searchParams],
  );
  const fallbackResponses = [
    "i can't help",
    "i'm sorry",
    "not sure",
    "unfortunately",
    "i don't have",
    "can't assist",
    "sorry, but",
  ];

  const sourceBtnRef = useRef<HTMLDivElement>(null);
  const messageSources =
    msg.sources || (currentIndex + 1 === message.length ? dataSource : []);
  const findMessage = (item) => {
    return item === msg;
  };

  const [loadingSeperation, setLoadingSeperation] = useState(false);
  const gradients = {
    dark: "bg-[linear-gradient(90deg,_rgba(34,34,34,0)_0%,_#222_66.82%)]",
    light: "bg-[linear-gradient(90deg,_rgba(255,255,255,0)_0%,_#FFF_66.82%)]",
  };

  useImperativeHandle(ref, () => {
    return {
      setLoadingSeperation,
    };
  }, []);

  let showCustomOptions = useMemo(
    () =>
      currentIndex === message.length - 1 &&
      !!botData?.aiBotConfig?.showFollowUps,
    [currentIndex, message],
  );

  const notificationSentRef = useRef(false);

  useEffect(() => {
    if (!embed) return;
    if (currentIndex !== message.length - 1) return;
    if (notificationSentRef.current || responseLoader || toolLoader) return;
    if (msg?.isLoader || msg.author !== 0 || !msg.aiResponse) return;
    notificationSentRef.current = true;
    const notificationTimer = setTimeout(() => {
      window.parent.postMessage(
        {
          type: "NEW_BOT_MESSAGE",
          timestamp: Date.now(),
          messagePreview:
            typeof msg.text === "string"
              ? msg.text.substring(0, 50)
              : msg.text?.value?.substring(0, 50) || "",
          hasAgent: !!msg.agent,
          botName: msg.agent?.name || botData.projectName,
          botIcon: botData.chatConfig.logo || null,
        },
        "*",
      );
    }, 100);

    return () => clearTimeout(notificationTimer);
  }, [embed, currentIndex, message.length, responseLoader, toolLoader, msg]);

  useEffect(() => {
    notificationSentRef.current = false;
  }, [currentIndex, message.length]);

  useEffect(() => {
    if (!embed) return;
    if (currentIndex !== message.length - 1) return;
    if (notificationSentRef.current) return;
    if (responseLoader || toolLoader) return;

    // Notify for human agent messagess
    if (msg?.agent && msg.author === 0 && !msg.isLoader && !msg.isTemp) {
      notificationSentRef.current = true;

      const notificationTimer = setTimeout(() => {
        window.parent.postMessage(
          {
            type: "NEW_AGENT_MESSAGE",
            timestamp: Date.now(),
            agent: msg.agent,
            messagePreview:
              typeof msg.text === "string"
                ? msg.text.substring(0, 50)
                : msg.text?.value?.substring(0, 50) || "",
            botName: msg.agent?.name || botData.projectName,
            botIcon: botData.chatConfig.logo || null,
          },
          "*",
        );
      }, 100);

      return () => clearTimeout(notificationTimer);
    }
  }, [embed, currentIndex, message.length, responseLoader, toolLoader, msg]);

  const [ogsMedia, setOgsMedia] = useState<{
    data: any[];
    isLoading: boolean;
    triedFetching: boolean;
  }>({ data: [], isLoading: false, triedFetching: false });
  const [ecommerceData, setEcommerceData] = useState<{
    data: any[];
    isLoading: boolean;
  }>({ data: [], isLoading: false });
  const [otpCollection, setOtpCollection] = useState<boolean>(false);
  const [isResendMessage, setIsResendMessage] = useState<boolean>(false);
  const [userContact, setUserContact] = useState<string>("");
  const currentContactRef = useRef<string>("");
  useEffect(() => {
    if (userContact && userContact.trim()) {
      currentContactRef.current = userContact;
    }
  }, [userContact]);

  const [cachedResponseIsPreserved, setCachedResponseIsPreserved] =
    useState<boolean>(false);
  const [updatedMessageText, setUpdatedMessageText] = useState<string | null>(
    null,
  );
  const currentMsg = useMemo(() => {
    return {
      ...msg,
      text: updatedMessageText !== null ? updatedMessageText : msg.text,
    };
  }, [msg, updatedMessageText]);
  const seperationTimoutId = useRef<any>(null);
  const clickableCustomOptions = useMemo(() => {
    const content = msg.text;
    const tag = "clickable-options-cbb";
    if (content.includes("<" + tag + ">")) {
      setLoadingSeperation(true);
      if (seperationTimoutId.current) clearTimeout(seperationTimoutId.current);

      seperationTimoutId.current = setTimeout(() => {
        setLoadingSeperation(false);
      }, 20000);
    }

    if (content.includes("</" + tag + ">")) setLoadingSeperation(false);

    return extractClickableOptionsData(content, tag, previousChat, steps);
  }, [msg.text, extractClickableOptionsData, steps]);

  const seperationData = useMemo(() => {
    if (msg.text && typeof msg.text === "string")
      return extractSeperationData(msg.text, "br-cbb", previousChat, steps);

    return null;
  }, [msg.text, extractSeperationData, steps]);

  const handoffSeparator = useMemo(() => {
    return msg.text.includes("<handoff>");
  }, [msg.text]);

  const getOGSLinkMetaData = useCallback(async (links) => {
    if (!links || !links?.length) return [];
    try {
      const { data } = await axiosPost(`/ogs/extract`, {
        urls: links,
        type: "basic",
      });

      return data.data;
    } catch (error) {
      console.error("Unable to get OGS metadata:", error);
      return links.map((link) => ({
        url: link,
      }));
    }
  }, []);
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "CACHE_RESPONSE_PRESERVED_TRUE") {
        console.log("Updating botData in Preview iframe:", event.data.botData);
        setCachedResponseIsPreserved(true);
        // currentMsg.text = event.data.currentMsgg;
        // setLocalMessageText(event.data.currentMsgg);
        if (event.data.responseCacheOptions?.id === responseCacheOptions?.id) {
          setUpdatedMessageText(event.data.currentMsgg);
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [responseCacheOptions?.id]);
  useEffect(() => {
    const content = String(msg.text ?? "");
    if (content.includes("<links-cbb>")) {
      setLoadingSeperation(true);
      if (seperationTimoutId.current) clearTimeout(seperationTimoutId.current);

      seperationTimoutId.current = setTimeout(() => {
        setLoadingSeperation(false);
      }, 20000);
    }

    let links = extractMediaLinks(content);
    links =
      links?.map((link) => {
        const match = link.match(/\(([^)]+)\)/);
        return match ? match[1] : link;
      }) || [];
    if (content.includes("</links-cbb>")) {
      setLoadingSeperation(false);
      if (
        !ogsMedia.isLoading &&
        !ogsMedia?.data.length &&
        !!links?.length &&
        !ogsMedia.triedFetching
      ) {
        setOgsMedia((prev) => ({ ...prev, isLoading: true }));
        getOGSLinkMetaData(links).then((data) => {
          setOgsMedia(() => {
            if (!data || !data.length) {
              return {
                data: links.map((link) => ({ url: link })),
                isLoading: false,
                triedFetching: true,
              };
            }
            return {
              data: data.map((item, i) => {
                if (!item?.url || item.success !== true) {
                  return {
                    ...item,
                    url: links[i],
                  };
                }
                return item;
              }),
              triedFetching: true,
              isLoading: false,
            };
          });
        });
      }
    }
  }, [msg.text, extractMediaLinks, getOGSLinkMetaData, ogsMedia]);

  // Extract ecommerce product data
  useEffect(() => {
    const content = String(msg.text ?? "");

    // Check if opening tag exists
    const hasOpeningTag =
      content.includes("<ecommerce-image>") ||
      content.includes("<ecommerce_image>");
    // Check if closing tag exists
    const hasClosingTag =
      content.includes("</ecommerce-image>") ||
      content.includes("</ecommerce_image>");

    if (hasOpeningTag && !hasClosingTag) {
      // Opening tag found but no closing tag - show loader
      if (!ecommerceData.isLoading) {
        setEcommerceData({ data: [], isLoading: true });
      }
    } else if (hasOpeningTag && hasClosingTag) {
      // Both tags present - extract and show products
      const products = extractEcommerceData(content);
      if (products.length > 0) {
        setEcommerceData({ data: products, isLoading: false });
      } else {
        setEcommerceData({ data: [], isLoading: false });
      }
    } else {
      // No ecommerce tags - reset
      if (ecommerceData.data.length > 0 || ecommerceData.isLoading) {
        setEcommerceData({ data: [], isLoading: false });
      }
    }
  }, [msg.text, extractEcommerceData]);

  useEffect(() => {
    const content = String(msg.text ?? "");
    if (checkOtpCollection(content).check) {
      setOtpCollection(true);
      setDisableInput(true);
      setUserContact(checkOtpCollection(content).contactExtract);
    }
    if (content.includes("<resend>")) {
      setIsResendMessage(true);
    }
  }, [msg.text, checkOtpCollection]);

  useEffect(() => {
    const content = String(msg.text ?? "");
    const otpData = checkOtpCollection(content);

    // console.log('[ChatComponent] OTP Check:', {
    //   check: otpData.check,
    //   contactExtract: otpData.contactExtract,
    //   type: otpData.type,
    //   content: content
    // });

    if (otpData.check) {
      setOtpCollection(true);
      setDisableInput(true);
      setUserContact(otpData.contactExtract);
    }
  }, [msg.text, checkOtpCollection]);

  function addNewUserMessage(value) {
    if (responseLoader) return;
    if (!showCustomOptions) return;
    showCustomOptions = false;
    handleUserReply(currentIndex, false, value);
  }

  // Calculate dynamic height for bot message
  const getBotMessageHeight = () => {
    if (currentIndex === message.length - 1) {
      // Get the actual user message element and calculate its height
      const userMessageElement = document.querySelector(
        ".currentLastFirstMessage",
      ) as HTMLElement;
      const userMessageHeightValue = userMessageElement
        ? userMessageElement.offsetHeight
        : 50;
      return userMessageHeightValue;
    }
    return "auto";
  };

  // Get previewContainer height
  const getPreviewContainerHeight = () => {
    const previewContainerElement = document.querySelector(
      ".previewContainer",
    ) as HTMLElement;
    const previewContainerHeight = previewContainerElement
      ? previewContainerElement.offsetHeight
      : 0;
    return previewContainerHeight;
  };

  // Get footer-sec height
  const getFooterSecHeight = () => {
    const footerSecElement = document.querySelector(
      ".footer-sec",
    ) as HTMLElement;
    const footerSecHeight = footerSecElement
      ? footerSecElement.offsetHeight
      : 0;
    return footerSecHeight;
  };

  // Check if we're in embed-view-container context
  const isEmbedViewContainer = () => {
    const embedViewContainer = document.querySelector(".embed-view-container");
    return embedViewContainer !== null;
  };

  // Check if we're in full-screen preview with mobile detection
  const isFullScreenMobilePreview = () => {
    const fullScreenWrapper = document.querySelector(
      ".cbb-full-screen-preview-wrapper",
    );
    return (
      fullScreenWrapper !== null &&
      fullScreenWrapper.classList.contains("cbb-mobile-live")
    );
  };

  // Check if we're in live-bot context
  const isLiveBot = () => {
    const liveBotElement = document.querySelector(".live-bot");
    return liveBotElement !== null;
  };

  // Auto-scroll for live-bot case
  // useEffect(() => {
  //   if (isLiveBot() && currentIndex === message.length - 1) {
  //     const previewContainer = document.querySelector(
  //       ".previewContainer.new-scrollbar.embed.fx-d-col.relative",
  //     );
  //     if (previewContainer) {
  //       previewContainer.scrollTop = previewContainer.scrollHeight;
  //     }
  //   }
  // }, [message, currentIndex]);
  // console.log("User message height:", getBotMessageHeight());
  // console.log("Preview container height:", getPreviewContainerHeight());
  // console.log("Footer-sec height:", getFooterSecHeight());
  // console.log(
  //   "Total height:",
  //   getPreviewContainerHeight() -
  //     (Number(getBotMessageHeight()) + getFooterSecHeight()),
  // );
  // console.log("Embed prop", embed);
  // console.log("Is embed-view-container", isEmbedViewContainer());
  // console.log("Is live-bot", isLiveBot());
  // console.log("Is last message", currentIndex === message.length - 1);

  return (
    (responseLoader ||
      toolLoader ||
      (typeof msg.text === "string" ? msg.text : msg.text?.value) !== "") && (
      <div
        className="fx w-full flex-col items-start justify-start gap-4"
        style={{
          minHeight:
            currentIndex === message.length - 1 &&
            (isEmbedViewContainer() || isFullScreenMobilePreview())
              ? `calc(${getPreviewContainerHeight()}px - (${getBotMessageHeight()}px + ${embed ? (message.length === 3 ? "50px" : "60px") : message.length === 3 ? "40px" : "50px"})` // after first query the scrolling is sightly off so message.length === 3 is here to handle this sepical case
              : "auto",
        }}
      >
        {(responseLoader ||
          toolLoader ||
          sanitizeCustomTags(
            typeof msg.text === "string" ? msg.text : msg.text?.value,
            null,
            steps,
          )) && (
          <motion.div
            key={currentIndex}
            className="cbb-bot-msg-wrapper botleft w-full"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className={`cbb-bot-icon-wrapper Previewthumbnail`}>
              <img
                className="cbb-bot-icon"
                src={
                  msg.agent?.source === "crisp"
                    ? "https://36-uploader-win.s3.amazonaws.com/KIfQtlRDC0T15imuQAYg_crisp_sq.png"
                    : chatConfig?.botAvatar?.isPreMade
                      ? preMadeBotAvatars[chatConfig?.botAvatar?.svgId - 1]
                      : chatConfig?.botAvatar?.url
                }
                alt="boticon"
              />
            </div>
            <div className="cbb-bot-msg-container fx fx-d-col width-calc100-40px gap-[4px]">
              <span
                style={{
                  color: getTextColor(chatConfig?.chatBgColor),
                }}
                className={`cbb-bot-name bot-name flex max-w-[250px] items-center !justify-start gap-[5px] text-[13px] font-bold leading-[110%] tracking-[-0.13px]`}
              >
                <span className="cbb-bot-name-text block max-w-[170px] truncate text-start">
                  {msg.agent?.name ?? botData?.projectName}
                </span>

                <svg
                  className="static"
                  width={4}
                  height={5}
                  viewBox="0 0 4 5"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx={2}
                    cy={2.5}
                    r={2}
                    fill={getTextColor(chatConfig?.chatBgColor)}
                  />
                </svg>
                <span
                  style={{
                    color: getTextColor(chatConfig?.chatBgColor),
                  }}
                  className={`cbb-bot-timestamp flex items-center whitespace-nowrap text-[12px] opacity-50`}
                >
                  {msg.timestamp
                    ? new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })
                    : "—"}
                </span>
              </span>
              <ReasoningDisplay
                reasoning={reasoning}
                chatConfig={chatConfig}
                isVisible={currentIndex === message.length - 1}
              />

              {sanitizeCustomTags(
                typeof msg.text === "string" ? msg.text : msg.text?.value,
                null,
                steps,
              ) && (
                <div
                  className={`cbb-bot-msg-box botChatBox`}
                  style={{
                    background: chatConfig?.color ?? "",
                  }}
                >
                  {msg.name === "button" ? (
                    <p
                      // className={
                      //   chatConfig?.theme === "dark" ? `dark-font` : `light-font`
                      // }
                      style={{
                        fontSize: chatConfig?.typography?.size + "px",
                        fontWeight: chatConfig?.typography?.weight,
                        color: getTextColor(chatConfig?.color),
                      }}
                      dangerouslySetInnerHTML={{
                        __html: wrapEmojisInHTML(
                          sanitizeCustomTags(
                            typeof msg.text === "string"
                              ? msg.text
                              : msg.text.value,
                            previousChat,
                            steps,
                          ),
                        ),
                      }}
                    />
                  ) : msg?.isLoader ? (
                    <ThreeDotLoader color={chatConfig?.typography?.color} />
                  ) : msg.showImg && msg.image ? (
                    <div className="cbb-question-button-answer questionButton-answer">
                      <img src={msg.image} alt="" />
                      <MarkdownContainer
                        chatConfig={chatConfig}
                        typography={typography}
                        msg={
                          currentIndex === message.length - 1 ? currentMsg : msg
                        }
                        setReadPdf={setReadPdf}
                        setReadPdfText={setReadPdfText}
                        responseLoader={responseLoader}
                        previousChat={previousChat}
                        steps={steps}
                      />
                    </div>
                  ) : (
                    <MarkdownContainer
                      chatConfig={chatConfig}
                      typography={typography}
                      msg={
                        currentIndex === message.length - 1 ? currentMsg : msg
                      }
                      setReadPdf={setReadPdf}
                      setReadPdfText={setReadPdfText}
                      responseLoader={responseLoader}
                      previousChat={previousChat}
                      steps={steps}
                    />
                  )}
                </div>
              )}

              {ogsMedia.isLoading ? (
                <div className="cbb-media-loading-wrapper mt-[5px] flex gap-[10px] overflow-hidden">
                  {[1, 2, 3].map((_, idx) => (
                    <div
                      key={idx}
                      className="cbb-media-skeleton-loading flex min-w-[250px] !max-w-[260px] animate-pulse items-center gap-[8px] rounded-[10px] border border-[rgba(0,0,0,0.07)] p-[8px]"
                      style={{
                        backgroundColor: deriveShade(
                          chatConfig?.chatBgColor || "#ffffff",
                          0.15,
                        ),
                      }}
                    >
                      <div
                        className="cbb-media-skeleton-box h-[49px] w-[76px] rounded-[3px]"
                        style={{
                          backgroundColor: deriveShade(
                            chatConfig?.chatBgColor || "#ffffff",
                            0.25,
                          ),
                        }}
                      />
                      <div className="flex flex-1 flex-col gap-[4px] overflow-hidden">
                        <div
                          className="mb-1 h-[14px] w-[60%] rounded"
                          style={{
                            backgroundColor: deriveShade(
                              chatConfig?.chatBgColor || "#ffffff",
                              0.25,
                            ),
                          }}
                        />
                        <div
                          className="h-[24px] w-[90%] rounded"
                          style={{
                            backgroundColor: deriveShade(
                              chatConfig?.chatBgColor || "#ffffff",
                              0.25,
                            ),
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : ogsMedia?.data.length ? (
                <div className="cbb-media-wrapper relative mt-[10px] overflow-x-auto overflow-y-hidden pe-[30px]">
                  {ogsMedia?.data.length >= 2 && (
                    <div
                      className={`cbb-fadeout-div1 fadeout-div1 absolute !start-[-20px] !top-0 z-10 !h-full !w-[56px] rotate-180`}
                      style={{
                        background: `linear-gradient(90deg, ${getColorWithAlpha(chatConfig?.chatBgColor, 0)} 0%, ${chatConfig?.chatBgColor} 66.82%)`,
                        rotate: chatConfig?.direction === "rtl" ? "180deg" : "",
                      }}
                    />
                  )}
                  <Swiper
                    modules={[]}
                    spaceBetween={10}
                    freeMode={true}
                    slidesPerView={"auto"}
                    grabCursor={true}
                    className="cbb-ogs-media-swiper ogsMedia-swiper"
                    // breakpoints={{
                    //   0: { slidesPerView: 1 },
                    //   640: { slidesPerView: 1 },
                    //   1024: { slidesPerView: 1 },
                    // }}
                  >
                    {ogsMedia?.data.map((media, index) => (
                      <SwiperSlide
                        key={index}
                        className="cbb-media-swiper-slide max-w-[260px]"
                      >
                        <li
                          className={`cbb-media-swiper-content flex min-h-[66px] min-w-[250px] !max-w-[260px] cursor-pointer items-center !justify-start gap-[8px] rounded-[10px] border border-[rgba(0,0,0,0.07)] p-[8px]`}
                          style={{
                            backgroundColor: `${deriveShade(chatConfig?.chatBgColor)}`,
                          }}
                          onClick={() => window.open(media.url, "_blank")}
                        >
                          <div
                            className={`flex h-[49px] w-[76px] min-w-[76px] items-center justify-center rounded-[6px]`}
                            style={{
                              backgroundColor: getTextColor(
                                chatConfig?.chatBgColor,
                              ),
                            }}
                          >
                            {[
                              ".pdf",
                              ".docx",
                              ".txt",
                              ".md",
                              ".xlsx",
                              ".csv",
                            ].some((ext) =>
                              media?.url?.toLowerCase().endsWith(ext),
                            ) ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                xmlnsXlink="http://www.w3.org/1999/xlink"
                                width={25}
                                height={25}
                                x={0}
                                y={0}
                                viewBox="0 0 24 24"
                                style={{
                                  enableBackground: "new 0 0 512 512",
                                }}
                                xmlSpace="preserve"
                                className=""
                                {...props}
                              >
                                <g>
                                  <path
                                    d="M17 14a1 1 0 0 1-1 1H8a1 1 0 0 1 0-2h8a1 1 0 0 1 1 1zm-4 3H8a1 1 0 0 0 0 2h5a1 1 0 0 0 0-2zm9-6.515V19a5.006 5.006 0 0 1-5 5H7a5.006 5.006 0 0 1-5-5V5a5.006 5.006 0 0 1 5-5h4.515a6.958 6.958 0 0 1 4.95 2.05l3.484 3.486A6.951 6.951 0 0 1 22 10.485zm-6.949-7.021A5.01 5.01 0 0 0 14 2.684V7a1 1 0 0 0 1 1h4.316a4.983 4.983 0 0 0-.781-1.05zM20 10.485c0-.165-.032-.323-.047-.485H15a3 3 0 0 1-3-3V2.047c-.162-.015-.321-.047-.485-.047H7a3 3 0 0 0-3 3v14a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3z"
                                    fill="#444444"
                                    opacity={1}
                                    data-original="#000000"
                                  />
                                </g>
                              </svg>
                            ) : media?.image ? (
                              <img
                                className="cbb-media-image h-full max-h-full max-w-full rounded-[6px] object-fill object-center"
                                src={media.image}
                                alt={media.title}
                              />
                            ) : media?.favicon ? (
                              <img
                                className="cbb-media-image max-h-full max-w-full rounded-[6px] object-cover object-center"
                                src={media.favicon}
                                alt={media.title}
                              />
                            ) : (
                              <Fallbackpreview className="cbb-media-image cbb-media-fallback-image h-full w-full rounded-[3px]" />
                            )}
                          </div>

                          <div className="cbb-media-content flex flex-col !items-start gap-[4px] overflow-hidden">
                            <h3
                              className={`cbb-media-title max-w-full overflow-hidden truncate text-[12px] font-normal leading-[13.2px]`}
                              style={{
                                color: getTextColor(
                                  deriveShade(chatConfig?.chatBgColor),
                                ),
                              }}
                            >
                              {media?.siteName || media.url}
                            </h3>

                            <p
                              className={`cbb-media-description line-clamp-2 !h-full text-[12px] font-medium not-italic leading-[15.6px]`}
                              style={{
                                color: getTextColor(
                                  deriveShade(chatConfig?.chatBgColor),
                                ),
                              }}
                            >
                              {media?.title || media?.description || ""}
                            </p>
                          </div>
                        </li>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  {ogsMedia?.data.length > 1 && (
                    <div
                      className={`cbb-media-fadeout absolute !end-[10px] !top-0 z-10 !h-full !w-[56px]`}
                      style={{
                        background: `linear-gradient(90deg, ${getColorWithAlpha(chatConfig?.chatBgColor, 0)} 0%, ${chatConfig?.chatBgColor} 66.82%)`,
                        rotate: chatConfig?.direction === "rtl" ? "180deg" : "",
                      }}
                    />
                  )}
                </div>
              ) : null}

              {/* Ecommerce Product Cards */}
              {ecommerceData?.isLoading ? (
                <div className="cbb-ecommerce-loading-wrapper mt-[10px]">
                  <div
                    className={`cbb-ecommerce-loader-box botChatBox`}
                    style={{ background: chatConfig?.color ?? "" }}
                  >
                    <ThreeDotLoader color={chatConfig?.typography?.color} />
                  </div>
                </div>
              ) : ecommerceData?.data.length > 0 ? (
                <div className="cbb-ecommerce-wrapper relative start-[-10px] mt-[10px] overflow-x-auto overflow-y-hidden px-[10px]">
                  {/* Left Navigation Button - Only show if more than 2 products */}
                  {ecommerceData?.data.length > 2 && (
                    <button
                      className="cbb-swiper-button-prev-custom swiper-button-prev-custom absolute start-[0px] top-1/2 z-10 flex h-[30px] w-[30px] -translate-y-1/2 items-center justify-center rounded-full border border-[rgba(0,0,0,0.1)] bg-white disabled:opacity-80"
                      aria-label="Previous"
                    >
                      <NavigationArrow rotate={0} />
                    </button>
                  )}

                  <Swiper
                    modules={[Navigation, Pagination]}
                    spaceBetween={10}
                    freeMode={true}
                    slidesPerView={"auto"}
                    grabCursor={true}
                    watchSlidesProgress={true}
                    navigation={
                      ecommerceData?.data.length > 2
                        ? {
                            prevEl:
                              ".cbb-swiper-button-prev-custom .swiper-button-prev-custom",
                            nextEl:
                              ".cbb-swiper-button-next-custom .swiper-button-next-custom",
                          }
                        : false
                    }
                    pagination={
                      ecommerceData?.data.length > 2
                        ? {
                            clickable: true,
                            el: ".cbb-swiper-pagination-custom .swiper-pagination-custom",
                            type: "bullets",
                            dynamicBullets: false,
                          }
                        : false
                    }
                    className="cbb-ecommerce-swiper ecommerce-swiper pb-[10px]"
                  >
                    {ecommerceData?.data.map((product, index) => {
                      // Extract product ID from URL if available
                      const productUrl = product.link || product.url || "#";
                      let productId = "";
                      try {
                        const url = new URL(productUrl);
                        productId = url.searchParams.get("p") || "";
                      } catch (e) {
                        // Invalid URL
                      }

                      // Normalize product data to match ProductCard interface
                      const normalizedProduct: EcommerceProduct = {
                        name: product.name || product.title || "Product",
                        image:
                          product.image ||
                          "https://via.placeholder.com/170x250?text=No+Image",
                        link: productUrl,
                        price: product.price || "Price not specified",
                        description: product.description || "",
                      };

                      return (
                        <SwiperSlide
                          key={index}
                          className="cbb-ecommerce-swiper-slide !me-[15px] max-w-[170px]"
                        >
                          <ProductCard
                            product={normalizedProduct}
                            chatConfig={chatConfig}
                            onBuyNow={(product) => {
                              // Send product details as a message in chat with ID if available
                              const productMessage = productId
                                ? `I want to buy ${product.name} (ID: ${productId}${product.price ? ", Price: " + product.price : ""})`
                                : `I want to buy ${product.name}${product.price ? " - " + product.price : ""}`;
                              handleUserReply(
                                message?.length ? message?.length - 1 : 0,
                                false,
                                productMessage,
                                false,
                              );
                            }}
                          />
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>

                  {/* Right Navigation Button - Only show if more than 2 products */}
                  {ecommerceData?.data.length > 2 && (
                    <button
                      className="cbb-swiper-button-next-custom swiper-button-next-custom absolute end-[0px] top-1/2 z-10 flex h-[30px] w-[30px] -translate-y-1/2 items-center justify-center rounded-full border border-[rgba(0,0,0,0.1)] bg-white disabled:opacity-80"
                      aria-label="Next"
                    >
                      <NavigationArrow rotate={180} />
                    </button>
                  )}
                  {/* Pagination dots - Only show if more than 2 products */}
                  {ecommerceData?.data.length > 2 && (
                    <div className="cbb-swiper-pagination-custom swiper-pagination-custom mt-[15px] flex justify-center gap-[8px]"></div>
                  )}
                </div>
              ) : null}

              {/* Payment Button */}
              {(() => {
                const fullTextContent =
                  typeof msg?.text === "string"
                    ? msg.text
                    : msg?.text?.value || "";

                if (fullTextContent && isPaymentButton(fullTextContent)) {
                  console.log(
                    "[ChatComponent] Payment button detected in message",
                  );
                  // Check if the payment-button tag is complete
                  const isComplete = isPaymentButtonComplete(fullTextContent);
                  console.log(
                    "[ChatComponent] Is payment button complete?",
                    isComplete,
                  );

                  // If streaming (incomplete tag), show loader
                  if (!isComplete) {
                    return (
                      <div className="cbb-payment-loading-wrapper mt-[10px]">
                        <div
                          className={`cbb-payment-loader-box botChatBox`}
                          style={{ background: chatConfig?.color ?? "" }}
                        >
                          <ThreeDotLoader
                            color={chatConfig?.typography?.color}
                          />
                        </div>
                      </div>
                    );
                  }

                  // Tag is complete, parse and render payment button
                  const paymentData = parsePaymentButton(fullTextContent);
                  console.log(
                    "[ChatComponent] Payment data parsed:",
                    paymentData,
                  );

                  if (paymentData) {
                    return (
                      <PaymentButton
                        url={paymentData.url}
                        order={paymentData.order}
                        amount={paymentData.amount}
                        chatConfig={chatConfig}
                        image={paymentData.image}
                      />
                    );
                  }
                }
                return null;
              })()}

              {otpCollection ? (
                <OTPCollection
                  chatConfig={chatConfig}
                  userContact={currentContactRef.current || userContact}
                  onVerify={async (otp) => {
                    setOtpCollection(false);
                    setDisableInput(false);
                    await handleUserReply(
                      message?.length ? message?.length - 1 : 0,
                      false,
                      otp,
                      false,
                    );
                  }}
                  onResend={async (newContact) => {
                    setOtpCollection(false);
                    setDisableInput(false);
                    if (newContact !== currentContactRef.current) {
                      setUserContact(newContact);
                      currentContactRef.current = newContact;
                    }
                    await handleUserReply(
                      message?.length ? message?.length - 1 : 0,
                      false,
                      newContact,
                      false,
                    );
                  }}
                  type={((): "email" | "phone" => {
                    const otpType = checkOtpCollection(
                      typeof msg.text === "string"
                        ? msg.text
                        : msg?.text?.value,
                    ).type;
                    return otpType === "phone" ? "phone" : "email";
                  })()}
                  language={botData.aiBotConfig?.language || "english"}
                  isLast={isLast}
                  isOverLimit={isOverLimit}
                  isResendMessage={isResendMessage}
                />
              ) : null}

              <div className="cbb-bot-actions bot-actions">
                {/* sentiment, regen, dataSource, copy buttons */}
                {message[message.length - 1].aiResponse &&
                  msg === message[message.length - 1] &&
                  !toolLoader &&
                  !ecommerceData?.data.length && (
                    <>
                      <div
                        className="cbb-copy-text-button copy-BotText cursor-pointer"
                        onClick={() => {
                          const textToCopy = sanitizeCustomTags(
                            typeof msg?.text === "string"
                              ? msg.text
                              : msg?.text?.value || "",
                            null,
                            steps,
                          );
                          try {
                            const textArea = document.createElement("textarea");
                            textArea.value = textToCopy;
                            textArea.style.top = "0";
                            textArea.style.left = "0";
                            textArea.style.position = "fixed";
                            textArea.style.opacity = "0";
                            document.body.appendChild(textArea);
                            textArea.focus();
                            textArea.select();
                            const successful = document.execCommand("copy");
                            document.body.removeChild(textArea);

                            if (
                              !successful &&
                              navigator?.clipboard?.writeText
                            ) {
                              navigator.clipboard
                                .writeText(textToCopy)
                                .catch(console.error);
                            }
                          } catch (err) {
                            if (navigator?.clipboard?.writeText) {
                              navigator.clipboard
                                .writeText(textToCopy)
                                .catch(console.error);
                            }
                          }
                        }}
                      >
                        <svg
                          className="cbb-copy-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="21"
                          height="20"
                          viewBox="0 0 21 20"
                          fill="none"
                        >
                          <path
                            d="M10.807 14.6667C11.6024 14.6657 12.3649 14.3582 12.9273 13.8114C13.4897 13.2646 13.8061 12.5233 13.807 11.75V6.64176C13.808 6.33513 13.7463 6.03138 13.6256 5.7481C13.5049 5.46482 13.3275 5.20766 13.1038 4.99151L11.7586 3.68368C11.5363 3.46619 11.2718 3.29378 10.9804 3.17642C10.689 3.05907 10.3766 2.9991 10.0612 3.00001H7.20703C6.41167 3.00094 5.64917 3.30852 5.08676 3.8553C4.52436 4.40208 4.20798 5.14341 4.20703 5.91667V11.75C4.20798 12.5233 4.52436 13.2646 5.08676 13.8114C5.64917 14.3582 6.41167 14.6657 7.20703 14.6667H10.807ZM5.40703 11.75V5.91667C5.40703 5.45255 5.59667 5.00743 5.93424 4.67924C6.2718 4.35105 6.72964 4.16668 7.20703 4.16668C7.20703 4.16668 10.1584 4.17484 10.207 4.18068V5.33334C10.207 5.64276 10.3335 5.93951 10.5585 6.1583C10.7835 6.37709 11.0888 6.50001 11.407 6.50001H12.5926C12.5986 6.54726 12.607 11.75 12.607 11.75C12.607 12.2141 12.4174 12.6593 12.0798 12.9874C11.7423 13.3156 11.2844 13.5 10.807 13.5H7.20703C6.72964 13.5 6.2718 13.3156 5.93424 12.9874C5.59667 12.6593 5.40703 12.2141 5.40703 11.75ZM16.207 7.66667V14.0833C16.2061 14.8566 15.8897 15.5979 15.3273 16.1447C14.7649 16.6915 14.0024 16.9991 13.207 17H7.80703C7.6479 17 7.49529 16.9385 7.38277 16.8291C7.27025 16.7197 7.20703 16.5714 7.20703 16.4167C7.20703 16.262 7.27025 16.1136 7.38277 16.0042C7.49529 15.8948 7.6479 15.8333 7.80703 15.8333H13.207C13.6844 15.8333 14.1423 15.649 14.4798 15.3208C14.8174 14.9926 15.007 14.5475 15.007 14.0833V7.66667C15.007 7.51196 15.0702 7.36359 15.1828 7.25419C15.2953 7.1448 15.4479 7.08334 15.607 7.08334C15.7662 7.08334 15.9188 7.1448 16.0313 7.25419C16.1438 7.36359 16.207 7.51196 16.207 7.66667Z"
                            fill={getTextColor(chatConfig?.chatBgColor)}
                          />
                        </svg>
                        <Tooltip
                          anchorSelector=".copy-BotText"
                          content="Click to copy!"
                          className="flex-cnt"
                          isRTL={rtl.isRTL}
                        />
                      </div>
                      <button
                        className={`cbb-regenerate-button stop-rgn-btn ${
                          chatConfig?.theme === "dark"
                            ? "stop-rgn-btn-dark"
                            : ""
                        } ${botData?.enableGemini ? "" : ""}`}
                        onClick={handleRegenerate}
                      >
                        <svg
                          className="cbb-regenerate-icon"
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <g clipPath="url(#clip0_3817_33822)">
                            <path
                              d="M11.6924 1.48832C11.6744 1.39036 11.6763 1.28978 11.6978 1.19254C11.7194 1.0953 11.7603 1.00338 11.818 0.922224C11.8757 0.841066 11.9492 0.772321 12.034 0.720062C12.1188 0.667803 12.2132 0.633092 12.3116 0.617986C12.4101 0.60288 12.5105 0.607685 12.6071 0.632118C12.7037 0.656551 12.7943 0.700114 12.8738 0.760227C12.9532 0.820339 13.0197 0.895779 13.0694 0.982077C13.1192 1.06837 13.1511 1.16378 13.1633 1.26263L13.6194 4.24004C13.6476 4.42416 13.6058 4.61218 13.5022 4.76697C13.3985 4.92175 13.2406 5.03206 13.0597 5.0761L9.99887 5.82045C9.90386 5.84356 9.80523 5.84773 9.7086 5.83272C9.61198 5.81771 9.51926 5.78381 9.43574 5.73297C9.35221 5.68212 9.27952 5.61533 9.22181 5.53639C9.1641 5.45745 9.12251 5.36792 9.0994 5.27291C9.07629 5.17789 9.07212 5.07926 9.08713 4.98264C9.10214 4.88601 9.13604 4.79329 9.18688 4.70977C9.23773 4.62624 9.30453 4.55355 9.38346 4.49584C9.4624 4.43813 9.55193 4.39654 9.64694 4.37343L11.0916 4.0221C9.086 2.56316 6.32356 2.71561 4.5913 4.44846C2.66729 6.37247 2.69111 9.56663 4.70801 11.5835C6.72491 13.6004 9.91848 13.6243 11.8431 11.7002C11.9827 11.5606 12.1721 11.4822 12.3695 11.4822C12.5669 11.4822 12.7563 11.5606 12.8959 11.7002C13.0355 11.8399 13.1139 12.0292 13.1139 12.2267C13.1139 12.4241 13.0355 12.6135 12.8959 12.7531C10.3621 15.2868 6.22471 15.2065 3.6552 12.6363C1.08569 10.0668 1.0053 5.92943 3.53848 3.39565C5.79536 1.13877 9.32657 0.956551 11.8871 2.76146L11.6924 1.48832Z"
                              fill={getTextColor(chatConfig?.chatBgColor)}
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_3817_33822">
                              <rect
                                width="15"
                                height="15"
                                fill="white"
                                transform="translate(0.765625 0.5)"
                              />
                            </clipPath>
                          </defs>
                        </svg>
                        <Tooltip
                          anchorSelector=".stop-rgn-btn"
                          content="Regenerate"
                          className="flex-cnt"
                          isRTL={rtl.isRTL}
                        />
                      </button>

                      {isPreviewMode ? (
                        <>
                          {/* add improve bot and starred reponse here */}
                          {responseCacheOptions && (
                            <div
                              className={`flex items-center justify-center gap-[5px] text-[12px] ${chatConfig?.theme === "dark" ? "text-white" : "text-[#666666]"}`}
                            >
                              {responseCacheOptions?.answered && (
                                <span
                                  className="fav-btn cursor-pointer"
                                  onClick={() => {
                                    if (!responseCacheOptions?.id) return;

                                    axiosPost(`/analysis/update`, {
                                      answer: "",
                                      question: "",
                                      cacheResponseId: responseCacheOptions?.id,
                                      preserved: !cachedResponseIsPreserved,
                                      fromPreservationMode: true,
                                    });
                                    setCachedResponseIsPreserved(
                                      !cachedResponseIsPreserved,
                                    );
                                  }}
                                >
                                  <FavoriteIcon
                                    size={16}
                                    isFilled={cachedResponseIsPreserved}
                                    emptyColor={getTextColor(
                                      chatConfig?.chatBgColor,
                                    )}
                                  />
                                  <Tooltip
                                    anchorSelector=".fav-btn "
                                    content="Add to Favourite"
                                    isRTL={rtl.isRTL}
                                  />
                                </span>
                              )}
                              {responseCacheOptions?.answered && (
                                <hr className="h-[15px] w-[1px]" />
                              )}
                              <span
                                className={`justify center flex cursor-pointer items-center`}
                                onClick={() =>
                                  // setOpenAddQuestionModal(true)
                                  {
                                    window.parent.postMessage(
                                      {
                                        type: "OPEN_ADD_QUESTION_MODAL",
                                        payload: {
                                          msg: currentMsg,
                                          responseCacheOptions,
                                        },
                                      },
                                      "*",
                                    );
                                  }
                                }
                              >
                                <svg
                                  className="mr-[5px]"
                                  width="15"
                                  height="14"
                                  viewBox="0 0 15 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M3.2627 0.0878906H8.5127C8.69526 0.0878906 8.87166 0.151026 9.01172 0.265625L9.07031 0.318359C9.21781 0.46598 9.30073 0.666317 9.30078 0.875C9.30078 1.05771 9.23684 1.23391 9.12207 1.37402L9.07031 1.43164C8.92263 1.57933 8.72155 1.66309 8.5127 1.66309H3.2627C2.41008 1.66326 1.7168 2.35696 1.7168 3.20898V8.45898L1.72461 8.61133C1.75491 8.9144 1.87439 9.20187 2.06738 9.4375L2.16992 9.55078C2.42355 9.80441 2.75653 9.96173 3.11035 9.99707L3.2627 10.0039H4.30566C4.44491 10.0039 4.58111 10.0413 4.7002 10.1104L4.81348 10.1895L7.00098 12.0322L7.05664 12.0791L7.11328 12.0322L9.3457 10.1846C9.48697 10.0681 9.66458 10.0039 9.84766 10.0039H10.8467C11.6991 10.0037 12.3923 9.3114 12.3926 8.45898V5.83398C12.3926 5.62513 12.4754 5.42405 12.623 5.27637C12.7707 5.12884 12.9709 5.0459 13.1797 5.0459C13.3625 5.0459 13.5386 5.10974 13.6787 5.22461L13.7363 5.27637C13.884 5.42405 13.9668 5.62513 13.9668 5.83398V8.45898C13.9665 10.179 12.5667 11.5789 10.8467 11.5791H10.1309L10.1074 11.5996L7.94434 13.3877L7.94141 13.3896C7.69715 13.6071 7.38189 13.7178 7.0625 13.7178C6.77679 13.7177 6.48859 13.6297 6.24512 13.4512L6.14355 13.3691L6.14258 13.3672L4.04297 11.5996L4.01855 11.5791H3.2627C1.54266 11.5789 0.142836 10.179 0.142578 8.45898V3.20898L0.146484 3.05371C0.182957 2.33428 0.46675 1.64926 0.950195 1.11523L1.05762 1.00293C1.60613 0.454464 2.33736 0.130841 3.1084 0.0917969L3.2627 0.0878906ZM12.7939 0.0888672C12.9691 0.0979121 13.1405 0.144174 13.2969 0.223633C13.4142 0.283252 13.5214 0.360582 13.6143 0.453125L13.7021 0.550781C14.0878 1.0221 14.0383 1.71806 13.6445 2.18945L13.5615 2.28125L9.64453 6.19824C9.47469 6.3681 9.28038 6.51115 9.06934 6.62402L8.85254 6.72656C8.55658 6.84914 8.23928 6.91309 7.91895 6.91309H7.36133C7.32064 6.91305 7.28136 6.90024 7.24805 6.87793L7.2168 6.85254C7.17884 6.8144 7.15735 6.7628 7.15723 6.70898V6.15137C7.15724 5.58514 7.35428 5.03858 7.70996 4.60449L7.87207 4.42578L11.8438 0.455078C11.9368 0.36201 12.0434 0.284169 12.1602 0.224609L12.2803 0.170898C12.4031 0.123732 12.5322 0.0958047 12.6631 0.0888672H12.7939Z"
                                    fill={getTextColor(chatConfig?.chatBgColor)}
                                    stroke="white"
                                    strokeWidth="0.175"
                                  />
                                </svg>
                                Improve Answer
                              </span>
                            </div>
                          )}
                          {/* {openAddQuestionModal &&
                            createPortal(
                              <AddQuestionModal
                                id={responseCacheOptions?.id}
                                onClose={() => setOpenAddQuestionModal(false)}
                                Answer={removeCustomTagsForPreview(currentMsg.text)}
                                type="update"
                                onSave={(updateAnswer) => {
                                  setCachedResponseIsPreserved(true);
                                  currentMsg.text = updateAnswer;
                                }}
                              />,
                              document.getElementById("dropdown-portal") as HTMLElement || document.body,
                           )} */}
                        </>
                      ) : (
                        <>
                          {responseCacheOptions && (
                            <div className="cbb-like-dislike-container like-dislike fx gap-[3px]">
                              {msg.reaction !== undefined ? (
                                <button
                                  className={`cbb-msg-liked-button flex items-center justify-center`}
                                >
                                  <div className="cbb-msg-liked-button-inner flex gap-[3px]">
                                    {msg.reaction !== false && (
                                      <motion.div
                                        className="cbb-msg-liked-button-svg-wrap flex aspect-square h-[28px] w-[28px] items-center justify-center"
                                        initial={{ scale: 1, rotate: 0 }}
                                        animate={
                                          msg.reaction === true
                                            ? { scale: 1, rotate: 360 }
                                            : { scale: 1, rotate: 0 }
                                        }
                                        transition={{
                                          type: "spring",
                                          stiffness: 300,
                                          damping: 10,
                                        }}
                                        whileTap={{ scale: 0.9 }}
                                        exit={{ scale: 0, rotate: -180 }}
                                      >
                                        {msg.reaction === true ? (
                                          <BlueLikeSVG />
                                        ) : (
                                          <LikeSVG
                                            className="cbb-like-msg-svg custom-like"
                                            fill={getTextColor(
                                              chatConfig?.chatBgColor,
                                            )}
                                          />
                                        )}
                                      </motion.div>
                                    )}

                                    {msg.reaction !== true && (
                                      <motion.div
                                        className="cbb-msg-disliked-button-svg-wrap flex aspect-square h-[28px] w-[28px] items-center justify-center"
                                        initial={{ scale: 1, rotate: 0 }}
                                        animate={
                                          msg.reaction === false
                                            ? { scale: 1, rotate: 360 }
                                            : { scale: 1, rotate: 0 }
                                        }
                                        transition={{
                                          type: "spring",
                                          stiffness: 300,
                                          damping: 10,
                                        }}
                                        whileTap={{ scale: 0.9 }}
                                        exit={{ scale: 0, rotate: -180 }}
                                      >
                                        {msg.reaction === false ? (
                                          <BlueDislike />
                                        ) : (
                                          <DislikeSVG className="custom-like" />
                                        )}
                                      </motion.div>
                                    )}
                                  </div>
                                </button>
                              ) : (
                                <>
                                  <button
                                    className="cbb-like-msg-button like-btn group relative flex aspect-square w-[28px] items-center justify-center"
                                    onClick={() => handleReaction(msg, true)}
                                  >
                                    <LikeSVG
                                      className="cbb-like-msg-svg custom-like"
                                      fill={getTextColor(
                                        chatConfig?.chatBgColor,
                                      )}
                                    />

                                    <Tooltip
                                      content="Good Response"
                                      anchorSelector=".like-btn"
                                      isRTL={rtl.isRTL}
                                    />
                                  </button>

                                  <button
                                    className="cbb-dislike-msg-button dislike-btn group relative flex aspect-square w-[28px] items-center justify-center"
                                    onClick={() => handleReaction(msg, false)}
                                  >
                                    <DislikeSVG
                                      className="cbb-dislike-msg-svg custom-like"
                                      fill={getTextColor(
                                        chatConfig?.chatBgColor,
                                      )}
                                    />

                                    <Tooltip
                                      content="Bad Response"
                                      anchorSelector=".dislike-btn"
                                      isRTL={rtl.isRTL}
                                    />
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      {botData?.showDataSource &&
                      messageSources?.length > 0 &&
                      !responseLoader ? (
                        <div
                          className="cbb-source-wrapper sourceBtn ms-[2px]"
                          ref={sourceBtnRef}
                        >
                          <Sources
                            dataSource={messageSources}
                            setReadPdf={setReadPdf}
                            setReadPdfText={setReadPdfText}
                            calledFromPreview={true}
                            responseLoader={responseLoader}
                            forwardedRef={sourceBtnRef}
                            isRTL={rtl.isRTL}
                          >
                            <svg
                              className="cbb-source-icon cursor-pointer"
                              width="21"
                              height="20"
                              viewBox="0 0 21 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M14.5098 3H5.75977C4.72602 3 3.88477 3.84125 3.88477 4.875V18H16.3848V4.875C16.3848 3.84125 15.5435 3 14.5098 3ZM5.75977 4.25H14.5098C14.8541 4.25 15.1348 4.53 15.1348 4.875V6.125H5.13477V4.875C5.13477 4.53 5.41539 4.25 5.75977 4.25ZM5.13477 16.75V7.375H15.1348V16.75H5.13477ZM7.00977 9.25H13.2598V10.5H7.00977V9.25ZM7.00977 11.75H10.7598V13H7.00977V11.75Z"
                                fill={getTextColor(chatConfig?.chatBgColor)}
                              />
                            </svg>
                          </Sources>
                        </div>
                      ) : null}

                      {/* </div> */}
                    </>
                  )}
              </div>
            </div>
          </motion.div>
        )}

        {currentIndex === message.length - 1 &&
          !responseLoader &&
          customGoalsMsg && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="cbb-bot-msg-wrapper botleft fx-d-col"
            >
              <div className="cbb-flex-gap-10 fx gap-10">
                <div className={`cbb-bot-icon-wrapper Previewthumbnail`}>
                  <img
                    src={
                      chatConfig?.botAvatar?.isPreMade
                        ? preMadeBotAvatars[chatConfig?.botAvatar?.svgId - 1]
                        : chatConfig?.botAvatar?.url
                    }
                    alt="boticon"
                  />
                </div>

                <div
                  className={`cbb-bot-msg-box botChatBox`}
                  style={{
                    background: chatConfig?.color ?? "",
                  }}
                >
                  <MarkdownContainer
                    chatConfig={chatConfig}
                    typography={typography}
                    msg={{ text: customGoalsMsg, aiResponse: true }}
                    setReadPdf={setReadPdf}
                    setReadPdfText={setReadPdfText}
                    responseLoader={responseLoader}
                    previousChat={previousChat}
                    steps={steps}
                  />
                </div>
              </div>
            </motion.div>
          )}

        <AnimatePresence>
          {seperationData &&
            seperationData.length > 0 &&
            seperationData.map((item, index) => (
              <motion.div
                key={index + "separate"}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="cbb-bot-msg-wrapper botleft fx-d-col ms-[43px]"
              >
                <div className="cbb-flex-gap-10 fx gap-10">
                  {/* <div
                    className={`cbb-bot-icon-wrapper Previewthumbnail ${
                      chatConfig?.theme === "dark" ? "dark-bg" : "light-bg"
                    }`}
                  >
                    <img
                      className="cbb-bot-icon"
                      src={
                        chatConfig?.botAvatar?.isPreMade
                          ? preMadeBotAvatars[chatConfig?.botAvatar?.svgId - 1]
                          : chatConfig?.botAvatar?.url
                      }
                      alt="boticon"
                    />
                  </div> */}

                  <div
                    className={`cbb-bot-msg-box botChatBox w-full`}
                    style={{
                      background: chatConfig?.color ?? "",
                    }}
                  >
                    <MarkdownContainer
                      chatConfig={chatConfig}
                      typography={typography}
                      msg={{ text: item, aiResponse: true }}
                      setReadPdf={setReadPdf}
                      setReadPdfText={setReadPdfText}
                      responseLoader={responseLoader}
                      previousChat={previousChat}
                      steps={steps}
                    />
                  </div>
                </div>
              </motion.div>
            ))}
        </AnimatePresence>

        {msg.name === "show_schedule_call" && (
          <div className="test mt-[-5px]">
            <div
              className={`cbb-bot-msg-box botChatBox ms-[45px] !p-[0px]`}
              style={{
                fontSize: chatConfig?.typography?.size + "px",
              }}
            >
              <p
                style={{
                  fontSize: typography?.size + "px",
                  fontWeight: typography?.weight,
                  color: getTextColor(chatConfig?.color),
                }}
              >
                <div
                  className="cbb-calendly-slot calendly_slot"
                  style={{
                    background: "transparent",
                    borderRadius: "10px",
                  }}
                >
                  <h3
                    className="fx items-center"
                    style={{
                      color: getTextColor(chatConfig?.chatBgColor),
                    }}
                  >
                    <img
                      src="https://dlvkyia8i4zmz.cloudfront.net/OhyL8ECnTUKPiWq1yCH7_Calendly__1_.svg"
                      alt="calendly icon"
                      className="h-[25px] w-[25px]"
                    />
                    Calendly's Booking Slot
                  </h3>
                  <PopupButton
                    url={calendlyConfig?.eventUrl}
                    rootElement={document.getElementById("root")!}
                    text={calendlyConfig?.btnText || "Book your Slot"}
                    prefill={{
                      email: userCredential?.email,
                      name: userCredential?.name,
                    }}
                  />
                </div>
              </p>
            </div>
          </div>
        )}
        {!hideClickableOptions &&
          showCustomOptions &&
          clickableCustomOptions &&
          !!clickableCustomOptions.length && (
            <div className="preview-starter-ques heyyy">
              <motion.ul
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: {
                    opacity: 0,
                  },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.1,
                      delayChildren: 0.2,
                    },
                  },
                }}
              >
                {clickableCustomOptions.map((content, index) => (
                  <motion.li
                    key={content}
                    variants={{
                      hidden: {
                        opacity: 0,
                        y: 20,
                        scale: 0.95,
                      },
                      visible: {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        transition: {
                          type: "spring",
                          stiffness: 300,
                          damping: 20,
                        },
                      },
                    }}
                    whileTap={{
                      scale: 0.98,
                      transition: {
                        type: "spring",
                        stiffness: 600,
                        damping: 25,
                      },
                    }}
                    onClick={() => {
                      addNewUserMessage(content);
                    }}
                    style={{
                      fontSize: chatConfig?.typography?.size + "px",
                      border: `1px solid ${getTextColor(chatConfig?.chatBgColor)}`,
                      color: getTextColor(chatConfig?.chatBgColor),
                      width: "auto",
                      cursor: "pointer",
                    }}
                  >
                    <span
                      style={{
                        fontSize: typography?.size + "px",
                        fontWeight: typography?.weight,
                        color: getTextColor(chatConfig?.chatBgColor),
                      }}
                      dangerouslySetInnerHTML={{
                        __html: wrapEmojisInHTML(content),
                      }}
                    />
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          )}

        {(loadingSeperation || (toolLoader && !msg?.isLoader)) && (
          <motion.div
            key="loading"
            className="cbb-bot-msg-wrapper botleft h-[34px] w-full"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div
              className={`cbb-bot-msg-box botChatBox ms-[40px] w-full`}
              style={{
                background: chatConfig?.color ?? "",
              }}
            >
              <ThreeDotLoader color={chatConfig?.typography?.color} />
            </div>
          </motion.div>
        )}
        {(botData?.aiStarterQues?.initialMsg ==
          message[message.length - 1]?.text &&
          botData?.aiStarterQues?.initialMsg == msg.text) ||
        (suggestedQuesAns &&
          message.findIndex(findMessage) === message.length - 1 &&
          !message[message.length - 1]?.text?.isMandatory) ? (
          <div className="cbb-clickable-options-wrapper preview-starter-ques heyyy">
            {
              botData && starterQues && starterQues.length > 0
              //  <h4 className={`${chatConfig?.theme === "dark" ? "dark-font" : "light-font"}`} style={{ fontFamily: chatConfig?.typography?.font?.fontFamily }}>SUGGESTED QUESTIONS</h4>
            }

            <AnimatePresence mode="wait">
              <motion.ul
                className="cbb-clickable-options-list"
                key={activeCat ? "categories" : "questions"}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={{
                  hidden: {
                    opacity: 0,
                  },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.1,
                      delayChildren: 0.2,
                    },
                  },
                  exit: {
                    opacity: 0,
                    transition: {
                      staggerChildren: 0.05,
                      staggerDirection: -1,
                    },
                  },
                }}
              >
                {botData &&
                  starterQues &&
                  botData?.aiStarterQues?.initialMsg?.trim().length > 0 &&
                  (!activeCat && botData?.aiStarterQues?.isCat
                    ? botData?.aiStarterQues?.cats.map((cat, index) => {
                        return (
                          <motion.li
                            className="cbb-clickable-option-item cbb-starter-ques-item"
                            key={`cat-${index}`}
                            variants={{
                              hidden: {
                                opacity: 0,
                                y: 20,
                                scale: 0.95,
                              },
                              visible: {
                                opacity: 1,
                                y: 0,
                                scale: 1,
                                transition: {
                                  type: "spring",
                                  stiffness: 300,
                                  damping: 20,
                                },
                              },
                              exit: {
                                opacity: 0,
                                y: -10,
                                scale: 0.95,
                                transition: {
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 25,
                                },
                              },
                            }}
                            whileTap={{
                              scale: 0.98,
                              transition: {
                                type: "spring",
                                stiffness: 600,
                                damping: 25,
                              },
                            }}
                            style={{
                              fontSize: chatConfig?.typography?.size + "px",

                              border: `1px solid ${getTextColor(chatConfig?.chatBgColor)}`,
                              color: getTextColor(chatConfig?.chatBgColor),
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              handleStarterQues(cat);
                            }}
                          >
                            <span
                              className="cbb-clickable-option-text"
                              style={{
                                fontSize: typography?.size + "px",
                                fontWeight: typography?.weight,
                                color: getTextColor(chatConfig?.chatBgColor),
                              }}
                              dangerouslySetInnerHTML={{
                                __html: wrapEmojisInHTML(cat),
                              }}
                            />
                          </motion.li>
                        );
                      })
                    : starterQues?.map((item, index) => {
                        return (
                          <motion.li
                            className="cbb-clickable-option-item cbb-starter-ques-item"
                            key={`ques-${index}`}
                            variants={{
                              hidden: {
                                opacity: 0,
                                y: 20,
                                scale: 0.95,
                              },
                              visible: {
                                opacity: 1,
                                y: 0,
                                scale: 1,
                                transition: {
                                  type: "spring",
                                  stiffness: 300,
                                  damping: 20,
                                },
                              },
                              exit: {
                                opacity: 0,
                                y: -10,
                                scale: 0.95,
                                transition: {
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 25,
                                },
                              },
                            }}
                            whileHover={{
                              scale: 1.02,
                              y: -2,
                              transition: {
                                type: "spring",
                                stiffness: 400,
                                damping: 15,
                              },
                            }}
                            whileTap={{
                              scale: 0.98,
                              transition: {
                                type: "spring",
                                stiffness: 600,
                                damping: 25,
                              },
                            }}
                            style={{
                              fontSize: chatConfig?.typography?.size + "px",
                              color: getTextColor(chatConfig?.chatBgColor),
                              border: `1px solid ${getTextColor(chatConfig?.chatBgColor)}`,
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              handleStarterQues(item?.ques);
                            }}
                          >
                            <span
                              className="cbb-clickable-option-text"
                              style={{
                                fontSize: typography?.size + "px",
                                fontWeight: typography?.weight,
                                color: getTextColor(chatConfig?.chatBgColor),
                              }}
                              dangerouslySetInnerHTML={{
                                __html: wrapEmojisInHTML(item?.ques),
                              }}
                            />
                          </motion.li>
                        );
                      }))}
              </motion.ul>
            </AnimatePresence>

            <AnimatePresence>
              {activeCat && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      type: "spring",
                      stiffness: 300,
                      damping: 25,
                      delay: 0.1,
                    },
                  }}
                  exit={{
                    opacity: 0,
                    y: -10,
                    transition: {
                      type: "spring",
                      stiffness: 400,
                      damping: 25,
                    },
                  }}
                >
                  <UserComponent
                    chatConfig={chatConfig}
                    msg={activeCat}
                    textColor={textColor}
                    embed={embed}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : null}
        {handoffSeparator && (
          <div className="cbb-handoff-separator wd-100 flex items-center gap-[15px]">
            <hr
              className={`cbb-handoff-separator-line wd-100 border`}
              style={{
                borderColor: getTextColor(chatConfig?.chatBgColor),
              }}
            />
            <span
              className={`cbb-handoff-separator-text flex-cnt w-[500px] text-[11px] uppercase`}
              style={{
                color: getTextColor(chatConfig?.chatBgColor),
              }}
            >
              Chat with live agent
            </span>
            <hr
              className={`cbb-handoff-separator-line wd-100 border`}
              style={{
                borderColor: getTextColor(chatConfig?.chatBgColor),
              }}
            />
          </div>
        )}
      </div>
    )
  );
});

function MarkdownContainer({
  chatConfig,
  typography,
  msg,
  setReadPdf,
  setReadPdfText,
  responseLoader,
  previousChat,
  steps,
}) {
  const textStyle = {
    fontSize: typography?.size + "px",
    fontFamily: typography?.font?.fontFamily,
    fontWeight: typography?.weight,
    color: typography?.color,
    background: `text ${typography?.color}`,
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  };

  const getBorderColor = (typographyColor: string | undefined): string => {
    if (!typographyColor) return "#ffffff";
    const match = typographyColor.match(/rgba?\([^)]+\)|#[0-9a-fA-F]{3,8}/);
    return match ? match[0] : typographyColor;
  };
  const rawText = typeof msg.text === "string" ? msg.text : msg.text?.value;
  // Removee ecommerce content, payment buttons, and clickable options from the text so they don't appear in markdown
  let messageText = removeEcommerceContent(rawText);
  messageText = removePaymentButton(messageText);
  messageText = removeClickableOptions(messageText);
  // Convert <br-cbb> to line breaks
  // messageText = convertBrCbbToLineBreaks(messageText);
  messageText = checkOtpCollection(messageText).msg;
  // Check if there's any actual text content after sanitization
  const hasTextContent = messageText && messageText.trim().length > 0;

  const headingComponents = {
    h1: ({ node, children, ...rest }: any) => (
      <h1 {...rest}>{processChildrenWithEmojis(children)}</h1>
    ),
    h2: ({ node, children, ...rest }: any) => (
      <h2 {...rest}>{processChildrenWithEmojis(children)}</h2>
    ),
    h3: ({ node, children, ...rest }: any) => (
      <h3 {...rest}>{processChildrenWithEmojis(children)}</h3>
    ),
    h4: ({ node, children, ...rest }: any) => (
      <h4 {...rest}>{processChildrenWithEmojis(children)}</h4>
    ),
    h5: ({ node, children, ...rest }: any) => (
      <h5 {...rest}>{processChildrenWithEmojis(children)}</h5>
    ),
    h6: ({ node, children, ...rest }: any) => (
      <h6 {...rest}>{processChildrenWithEmojis(children)}</h6>
    ),
  };

  return (
    <>
      {hasTextContent && (
        <p className="hi cbb-bot-msg-text" style={textStyle}>
          {typeof msg.text === "string" ? (
            !msg?.aiResponse ? (
              <Markdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[
                  rehypeRaw,
                  [
                    rehypeExternalLinks,
                    {
                      target: "_blank",
                      properties: { class: "response-link" },
                    },
                  ],
                ]}
                components={{
                  ...headingComponents,
                  li(props) {
                    const { children, className, node, ...rest } = props;
                    const match = /task-list-item/.exec(className || "");

                    return match ? null : (
                      <li
                        {...rest}
                        className={(className || "") + " mb-[4px]"}
                        style={textStyle}
                      >
                        {processChildrenWithEmojis(children)}
                      </li>
                    );
                  },
                  p(props) {
                    const { children, ...rest } = props;
                    const getTextContent = (node: any): string => {
                      if (typeof node === "string") return node;
                      if (Array.isArray(node))
                        return node.map(getTextContent).join("");
                      if (node?.props?.children)
                        return getTextContent(node.props.children);
                      return "";
                    };

                    const text = getTextContent(children);
                    if (isOnlyEmoji(text)) {
                      return (
                        <p
                          className="markdown-gap cbb-emoji-only-paragraph"
                          {...rest}
                          style={{
                            display: "inline-block",
                            background: "none",
                            backgroundClip: "initial",
                            WebkitBackgroundClip: "initial",
                            WebkitTextFillColor: "initial",
                            color: "inherit",
                            fontSize: typography?.size + "px",
                          }}
                        >
                          {text}
                        </p>
                      );
                    }

                    return (
                      <p className="markdown-gap" {...rest} style={textStyle}>
                        {processChildrenWithEmojis(children)}
                      </p>
                    );
                  },
                }}
              >
                {messageText}
              </Markdown>
            ) : (
              <Markdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[
                  rehypeRaw,
                  [
                    rehypeExternalLinks,
                    {
                      target: "_blank",
                      properties: { class: "response-link" },
                    },
                  ],
                ]}
                components={{
                  ...headingComponents,
                  code(props) {
                    const { children, className, node, ...rest } = props;
                    const match = /language-(\w+)/.exec(className || "");
                    const language = match?.[1] || "javascript";
                    const codeString = String(children).replace(/\n$/, "");
                    const bgColor = getTextColor(chatConfig?.color);
                    const textColor = getTextColor(
                      getTextColor(chatConfig?.color),
                    );

                    return (
                      <SyntaxHighlighter
                        language={language}
                        style={{
                          hljs: {
                            display: "block",
                            overflowX: "auto",
                            padding: "1em",
                            background: bgColor,
                            color: textColor,
                          },
                        }}
                        PreTag="div"
                        wrapLongLines
                        className="code-block"
                      >
                        {codeString}
                      </SyntaxHighlighter>
                    );
                  },
                  table(props) {
                    const { children, className, node, ...rest } = props;
                    return (
                      <div
                        style={{
                          overflowX: "auto",
                          overflowY: "hidden",
                          margin: "16px 0",
                          borderRadius: "8px",
                          border: `1px solid ${getBorderColor(typography?.color)}`,
                          width: "100%",
                          maxWidth: "100%",
                          scrollbarWidth: "thin",
                          scrollbarColor:
                            chatConfig?.theme === "dark"
                              ? "#666 #333"
                              : "#aaa #f4f4f4",
                        }}
                        className="table-horizontal-scroll"
                      >
                        <table
                          {...rest}
                          className={className}
                          style={{
                            width: "100%",
                            minWidth: "max-content",
                            borderCollapse: "collapse",
                            fontSize: typography?.size + "px",
                            color: getBorderColor(typography?.color),
                            tableLayout: "auto",
                          }}
                        >
                          {children}
                        </table>
                      </div>
                    );
                  },
                  thead(props) {
                    const { children, className, node, ...rest } = props;
                    return (
                      <thead
                        {...rest}
                        className={className}
                        // style={{
                        //   backgroundColor:
                        //     chatConfig?.theme === "dark" ? "#374151" : "#f9fafb",
                        // }}
                      >
                        {children}
                      </thead>
                    );
                  },
                  tbody(props) {
                    const { children, className, node, ...rest } = props;
                    return (
                      <tbody {...rest} className={className}>
                        {children}
                      </tbody>
                    );
                  },
                  tr(props) {
                    const { children, className, node, ...rest } = props;
                    return (
                      <tr
                        {...rest}
                        className={className}
                        style={{
                          borderBottom: `1px solid ${getBorderColor(typography?.color)}`,
                          transition: "background-color 0.2s ease",
                        }}
                      >
                        {children}
                      </tr>
                    );
                  },
                  th(props) {
                    const { children, className, node, ...rest } = props;
                    return (
                      <th
                        {...rest}
                        className={className}
                        style={{
                          padding: "8px 12px",
                          textAlign: "left",
                          fontWeight: "600",
                          borderBottom: `2px solid ${getBorderColor(typography?.color)}`,
                          whiteSpace: "nowrap",
                          minWidth: "fit-content",
                        }}
                      >
                        {children}
                      </th>
                    );
                  },
                  td(props) {
                    const { children, className, node, ...rest } = props;
                    return (
                      <td
                        {...rest}
                        className={className}
                        style={{
                          padding: "8px 12px",
                          verticalAlign: "top",
                          whiteSpace: "nowrap",
                          minWidth: "fit-content",
                        }}
                      >
                        {children}
                      </td>
                    );
                  },
                  ol(props) {
                    const { children, className, node, ...rest } = props;
                    const match = /task-list/.exec(className || "");
                    return match ? null : (
                      <ol
                        {...rest}
                        className={className}
                        style={{
                          listStyleType: "decimal",
                          paddingBlock: "3px",
                          ...textStyle,
                        }}
                      >
                        {children}
                      </ol>
                    );
                  },
                  ul(props) {
                    const { children, className, node, ...rest } = props;
                    const match = /task-list/.exec(className || "");
                    return match ? null : (
                      <ul
                        {...rest}
                        className={className}
                        style={{
                          fontSize: typography?.size + "px",
                          paddingBlock: "3px",
                          color: getTextColor(chatConfig?.color),
                        }}
                      >
                        {children}
                      </ul>
                    );
                  },
                  li(props) {
                    const { children, className, node, ...rest } = props;
                    const match = /task-list-item/.exec(className || "");

                    return match ? null : (
                      <li
                        {...rest}
                        className={(className || "") + " mb-[4px]"}
                        style={textStyle}
                      >
                        {processChildrenWithEmojis(children)}
                      </li>
                    );
                  },
                  p(props) {
                    const { children, ...rest } = props;
                    const getTextContent = (node: any): string => {
                      if (typeof node === "string") return node;
                      if (Array.isArray(node))
                        return node.map(getTextContent).join("");
                      if (node?.props?.children)
                        return getTextContent(node.props.children);
                      return "";
                    };

                    const text = getTextContent(children);
                    if (isOnlyEmoji(text)) {
                      return (
                        <p
                          className="markdown-gap cbb-emoji-only-paragraph"
                          {...rest}
                          style={{
                            display: "inline-block",
                            background: "none",
                            backgroundClip: "initial",
                            WebkitBackgroundClip: "initial",
                            WebkitTextFillColor: "initial",
                            color: "inherit",
                            fontSize: typography?.size + "px",
                          }}
                        >
                          {text}
                        </p>
                      );
                    }

                    return (
                      <p className="markdown-gap" {...rest} style={textStyle}>
                        {processChildrenWithEmojis(children)}
                      </p>
                    );
                  },
                }}
              >
                {sanitizeCustomTags(messageText, null, steps)}
              </Markdown>
            )
          ) : (
            <span
              style={textStyle}
              dangerouslySetInnerHTML={{
                __html: sanitizeCustomTags(messageText, previousChat, steps),
              }}
            />
          )}
        </p>
      )}
    </>
  );
}

export function UserComponent(props: any) {
  const {
    currentIndex,
    chatConfig,
    msg,
    textColor,
    embed,
    previousChat,
    isCurrentLastUserMessage,
  } = props;
  const typography = chatConfig?.typography;
  const preMadeUserAvatars = [
    "https://dlvkyia8i4zmz.cloudfront.net/1JBvDAMgT2KgWxO8PvIi_1__1_.png",
    "https://dlvkyia8i4zmz.cloudfront.net/Nex2Ug4UQMGD75DzH2y5_2__1_.png",
    "https://dlvkyia8i4zmz.cloudfront.net/pxWqkGgTR5eEAEoguLXg_3__1_.png",
    "https://dlvkyia8i4zmz.cloudfront.net/lZQjg7omTQe8IGZ4nL8B_4__1_.png",
  ];

  const Avatar = chatConfig?.showUserAvatar ? (
    !chatConfig?.userAvatar?.isPreMade ? (
      <div
        className={`${embed ? "cbb-user-icon-wrapper preview-thb-right" : "Previewthumbnail"} `}
      >
        <img src={chatConfig?.userAvatar?.url} alt="usericon" />
      </div>
    ) : (
      <div
        className={`${embed ? "cbb-user-icon-wrapper preview-thb-right" : "Previewthumbnail"} `}
      >
        <img
          className="cbb-user-icon"
          src={preMadeUserAvatars[chatConfig?.userAvatar?.svgId - 1]}
          alt="usericon"
        />
      </div>
    )
  ) : null;

  return (
    (typeof msg.text === "string" ? msg.text : msg.text?.value) !==
      "NO_REPLY" && (
      <motion.div
        key={currentIndex}
        className={`cbb-user-msg-wrapper userright ${isCurrentLastUserMessage ? "currentLastFirstMessage" : ""}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <>
          <div
            className={`cbb-user-msg-box userChatBox`}
            style={{ background: chatConfig?.userBubbleColor }}
          >
            <p
              style={{
                fontSize: typography?.size + "px",
                color: getTextColor(chatConfig?.userBubbleColor),
              }}
              className={`cbb-user-msg-text`}
              // dangerouslySetInnerHTML={{
              //   __html:
              //     typeof msg.text === "string"
              //       ? msg.text
              //       : msg.text?.value
              // }}
            >
              {typeof msg.text === "string" ? msg.text : msg.text?.value}
            </p>
          </div>
          {Avatar}
        </>
      </motion.div>
    )
  );
}

export const FavoriteIcon = ({
  isFilled,
  fillColor,
  size,
  emptyColor,
}: {
  isFilled?: boolean;
  fillColor?: string;
  size?: number;
  emptyColor?: string;
}) => {
  const DEFAULT_COLOR = "#FD9D03";
  const color = fillColor || DEFAULT_COLOR;

  return (
    <svg
      width={size || "18"}
      height={size || "18"}
      viewBox="0 0 18 18"
      fill={isFilled ? color : "none"}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.293 5.82715L11.4668 6.18066L11.8574 6.2373L16.9873 6.98145L13.2764 10.6006L12.9941 10.876L13.0605 11.2646L13.9355 16.374L9.34766 13.9619L8.99805 13.7783L8.64941 13.9619L4.05957 16.374L4.9375 11.2637L5.00391 10.875L4.72168 10.5996L1.00781 6.98145L6.13965 6.2373L6.5293 6.18066L6.7041 5.82715L8.99805 1.17773L11.293 5.82715Z"
        stroke={isFilled ? color : emptyColor}
        strokeOpacity="1"
        strokeWidth="1.5"
      />
    </svg>
  );
};

export const BlueLikeSVG = () => {
  return (
    <svg
      className="cbb-msg-liked-icon"
      width="16"
      height="14"
      viewBox="0 0 16 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.7331 5.32476C14.4401 4.98702 14.0778 4.71619 13.671 4.53061C13.2641 4.34503 12.8222 4.24904 12.375 4.24914H9.88188L10.0919 2.97351C10.1662 2.5243 10.0765 2.06335 9.83929 1.67472C9.60205 1.2861 9.23301 0.995708 8.7995 0.856507C8.36598 0.717307 7.89687 0.738575 7.47774 0.916434C7.0586 1.09429 6.71735 1.41689 6.51625 1.82539L5.5 3.88476V13.6241H11.9375C12.6897 13.6211 13.4157 13.3481 13.9836 12.8549C14.5515 12.3616 14.9235 11.681 15.0319 10.9366L15.4725 7.81164C15.5348 7.36834 15.501 6.91683 15.3734 6.48774C15.2459 6.05865 15.0275 5.66202 14.7331 5.32476ZM0.5 7.37414V10.4991C0.500992 11.3276 0.830551 12.1219 1.41639 12.7078C2.00222 13.2936 2.7965 13.6231 3.625 13.6241H4.25V4.24914H3.625C2.7965 4.25013 2.00222 4.57969 1.41639 5.16552C0.830551 5.75136 0.500992 6.54564 0.5 7.37414Z"
        fill="#2C81FE"
      />
    </svg>
  );
};

export const BlueDislike = () => {
  return (
    <svg
      className="cbb-msg-disliked-button-svg"
      width="15"
      height="15"
      viewBox="0 0 15 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1292_904)">
        <path
          d="M14.9694 7.6875L14.5288 4.5625C14.4204 3.81871 14.0489 3.13844 13.4817 2.64529C12.9144 2.15214 12.1891 1.87883 11.4375 1.875H5V11.615L6.01625 13.6775C6.21798 14.0852 6.55935 14.4069 6.97826 14.5841C7.39717 14.7613 7.86578 14.7823 8.29883 14.6432C8.73189 14.504 9.10061 14.2141 9.33791 13.826C9.57522 13.438 9.66534 12.9776 9.59188 12.5287L9.38188 11.25H11.875C12.3224 11.25 12.7645 11.154 13.1716 10.9684C13.5786 10.7828 13.9411 10.512 14.2344 10.1743C14.5278 9.83651 14.7452 9.43972 14.872 9.01071C14.9988 8.58169 15.032 8.13045 14.9694 7.6875ZM0 5V8.125C0.000992411 8.9535 0.330551 9.74778 0.916388 10.3336C1.50222 10.9194 2.2965 11.249 3.125 11.25H3.75V1.875H3.125C2.2965 1.87599 1.50222 2.20555 0.916388 2.79139C0.330551 3.37722 0.000992411 4.1715 0 5Z"
          fill="#2C81FE"
        />
      </g>
      <defs>
        <clipPath id="clip0_1292_904">
          <rect width="15" height="15" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const LikeSVG = ({ className, fill = "#66666" }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="14"
    viewBox="0 0 16 14"
    fill="none"
  >
    <path
      d="M14.9978 5.12945C14.7047 4.7917 14.3425 4.52087 13.9356 4.3353C13.5288 4.14972 13.0868 4.05373 12.6396 4.05382H10.1465L10.3565 2.7782C10.4308 2.32899 10.3412 1.86803 10.1039 1.47941C9.86669 1.09079 9.49766 0.800395 9.06415 0.661195C8.63063 0.521994 8.16152 0.543263 7.74238 0.721122C7.32324 0.898981 6.982 1.22158 6.7809 1.63007L5.58465 4.05382H3.88965C3.06115 4.05482 2.26687 4.38438 1.68104 4.97021C1.0952 5.55605 0.765641 6.35033 0.764648 7.17882L0.764648 10.3038C0.765641 11.1323 1.0952 11.9266 1.68104 12.5124C2.26687 13.0983 3.06115 13.4278 3.88965 13.4288H12.2021C12.9543 13.4257 13.6804 13.1528 14.2483 12.6595C14.8162 12.1663 15.1881 11.4856 15.2965 10.7413L15.7371 7.61632C15.7992 7.17303 15.7653 6.72158 15.6377 6.29253C15.5102 5.86348 15.2919 5.46684 14.9978 5.12945ZM2.01465 10.3038V7.17882C2.01465 6.68154 2.21219 6.20463 2.56382 5.853C2.91545 5.50137 3.39237 5.30382 3.88965 5.30382H5.13965V12.1788H3.88965C3.39237 12.1788 2.91545 11.9813 2.56382 11.6297C2.21219 11.278 2.01465 10.8011 2.01465 10.3038ZM14.4965 7.4407L14.0553 10.5657C13.9908 11.0119 13.7682 11.4202 13.4281 11.7162C13.0881 12.0123 12.653 12.1764 12.2021 12.1788H6.38965V5.13757C6.44855 5.08626 6.49731 5.02436 6.5334 4.95507L7.90152 2.1832C7.95283 2.09066 8.02536 2.01161 8.11317 1.95255C8.20097 1.89349 8.30153 1.85612 8.40659 1.84349C8.51165 1.83086 8.6182 1.84334 8.7175 1.8799C8.81679 1.91647 8.906 1.97608 8.97777 2.05382C9.03918 2.12523 9.08407 2.2093 9.10924 2.30005C9.13441 2.3908 9.13925 2.48599 9.1234 2.57882L8.7934 4.57882C8.77891 4.66819 8.78402 4.75963 8.80835 4.84683C8.83268 4.93402 8.87567 5.01489 8.93434 5.08384C8.993 5.15279 9.06595 5.20817 9.14812 5.24615C9.2303 5.28414 9.31974 5.30381 9.41027 5.30382H12.6396C12.908 5.30379 13.1733 5.36137 13.4175 5.47267C13.6617 5.58397 13.8792 5.74639 14.0552 5.94897C14.2312 6.15155 14.3617 6.38956 14.4379 6.64691C14.514 6.90426 14.534 7.17495 14.4965 7.4407Z"
      fill={fill}
    />
  </svg>
);
export const DislikeSVG = ({ className, fill = "#66666" }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <g clipPath="url(#clip0_3817_33818)">
      <path
        d="M15.735 8.1875L15.2944 5.0625C15.1861 4.31871 14.8145 3.63844 14.2473 3.14529C13.6801 2.65214 12.9548 2.37883 12.2031 2.375H3.89062C3.06213 2.37599 2.26785 2.70555 1.68201 3.29139C1.09618 3.87722 0.766617 4.6715 0.765625 5.5L0.765625 8.625C0.766617 9.4535 1.09618 10.2478 1.68201 10.8336C2.26785 11.4194 3.06213 11.749 3.89062 11.75H5.58562L6.78188 14.1737C6.98298 14.5822 7.32422 14.9048 7.74336 15.0827C8.1625 15.2606 8.63161 15.2818 9.06512 15.1426C9.49864 15.0034 9.86767 14.713 10.1049 14.3244C10.3422 13.9358 10.4318 13.4748 10.3575 13.0256L10.1475 11.75H12.6406C13.088 11.75 13.5301 11.654 13.9372 11.4684C14.3442 11.2828 14.7067 11.012 15 10.6743C15.2934 10.3365 15.5108 9.93972 15.6376 9.51071C15.7644 9.08169 15.7976 8.63045 15.735 8.1875ZM3.89062 3.625H5.14062V10.5H3.89062C3.39334 10.5 2.91643 10.3025 2.5648 9.95083C2.21317 9.59919 2.01562 9.12228 2.01562 8.625V5.5C2.01562 5.00272 2.21317 4.52581 2.5648 4.17417C2.91643 3.82254 3.39334 3.625 3.89062 3.625ZM14.0556 9.855C13.8796 10.0575 13.6623 10.2198 13.4181 10.3311C13.174 10.4424 12.9089 10.5 12.6406 10.5H9.41125C9.3206 10.5 9.23102 10.5197 9.14875 10.5577C9.06647 10.5958 8.99345 10.6513 8.93477 10.7204C8.87608 10.7895 8.83312 10.8705 8.80888 10.9579C8.78464 11.0452 8.77969 11.1368 8.79437 11.2263L9.12437 13.2262C9.14022 13.3191 9.13539 13.4143 9.11022 13.505C9.08504 13.5958 9.04016 13.6798 8.97875 13.7512C8.90675 13.8289 8.81735 13.8883 8.71791 13.9246C8.61847 13.9609 8.51182 13.9731 8.40675 13.9602C8.30167 13.9473 8.20117 13.9096 8.1135 13.8502C8.02584 13.7908 7.95352 13.7115 7.9025 13.6187L6.53437 10.8488C6.49828 10.7795 6.44953 10.7176 6.39062 10.6663V3.625H12.2031C12.6545 3.62665 13.0903 3.79046 13.4311 4.08656C13.7718 4.38267 13.9948 4.79135 14.0594 5.23812L14.5006 8.36313C14.5376 8.62916 14.5171 8.90004 14.4403 9.15743C14.3635 9.41482 14.2323 9.65271 14.0556 9.855Z"
        fill={fill}
      />
    </g>
    <defs>
      <clipPath id="clip0_3817_33818">
        <rect
          width="15"
          height="15"
          fill="white"
          transform="translate(0.765625 0.5)"
        />
      </clipPath>
    </defs>
  </svg>
);

export const SmCalendlyIcon = () => (
  <svg
    className="cbb-sm-calendly-icon"
    width={25}
    height={24}
    viewBox="0 0 25 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_206_1531)">
      <path
        d="M16.9518 15.5597C16.1751 16.235 15.2088 17.0753 13.4484 17.0753H12.3976C11.1256 17.0753 9.96871 16.6226 9.14105 15.8002C8.33282 14.9967 7.88643 13.8981 7.88643 12.7049V11.2938C7.88643 10.1006 8.33177 9.00091 9.14105 8.19846C9.96923 7.37605 11.1256 6.92336 12.3976 6.92336H13.4484C15.2083 6.92336 16.1751 7.76362 16.9518 8.43898C17.7574 9.13955 18.4533 9.74454 20.3071 9.74454C20.5949 9.74454 20.8774 9.72196 21.1521 9.67837C21.15 9.67259 21.1479 9.66787 21.1458 9.66262C21.0355 9.39478 20.9058 9.13115 20.7561 8.87697L19.5167 6.77106C18.3797 4.83846 16.2775 3.64844 14.0035 3.64844H11.5242C9.25028 3.64844 7.14805 4.83898 6.01107 6.77106L4.77168 8.87697C3.6347 10.8096 3.6347 13.1896 4.77168 15.1217L6.01107 17.2276C7.14805 19.1602 9.25028 20.3497 11.5242 20.3497H14.0035C16.2775 20.3497 18.3797 19.1592 19.5167 17.2276L20.7561 15.1217C20.9058 14.867 21.0355 14.6044 21.1458 14.3366C21.1479 14.3308 21.15 14.3261 21.1521 14.3208C20.8774 14.2772 20.5959 14.2547 20.3071 14.2547C18.4538 14.2541 17.7574 14.8591 16.9518 15.5597Z"
        fill="#3664AE"
      />
      <path
        d="M13.4485 8.20312H12.3977C10.4619 8.20312 9.18945 9.55962 9.18945 11.2958V12.7069C9.18945 14.4431 10.4614 15.7996 12.3977 15.7996H13.4485C16.2692 15.7996 16.0476 12.9784 20.3067 12.9784C20.7147 12.9784 21.118 13.0152 21.5103 13.0866C21.6395 12.3693 21.6395 11.6351 21.5103 10.9172C21.1175 10.9886 20.7147 11.0254 20.3067 11.0254C16.0476 11.0248 16.2692 8.20312 13.4485 8.20312Z"
        fill="#3664AE"
      />
      <path
        d="M23.9565 14.1158C23.2292 13.5896 22.3947 13.2462 21.5098 13.0844C21.5082 13.0912 21.5077 13.0981 21.5061 13.1049C21.4305 13.5182 21.3123 13.9252 21.1506 14.3201C21.8974 14.4388 22.5853 14.714 23.1735 15.1367C23.1714 15.143 23.1698 15.1493 23.1677 15.1562C22.8285 16.2364 22.317 17.2547 21.6484 18.1811C20.9878 19.096 20.189 19.9016 19.2736 20.5743C17.3736 21.9723 15.1107 22.7107 12.7285 22.7107C11.2539 22.7107 9.82385 22.4276 8.47785 21.8693C7.17807 21.33 6.0101 20.5575 5.00599 19.5728C4.0024 18.5881 3.21518 17.4427 2.66533 16.1671C2.09658 14.8469 1.80774 13.4441 1.80774 11.9973C1.80774 10.5505 2.09658 9.14777 2.66533 7.82751C3.21518 6.55241 4.0024 5.4065 5.00599 4.42182C6.00958 3.43713 7.17754 2.66514 8.47785 2.12527C9.82332 1.56702 11.2533 1.28396 12.7285 1.28396C15.1107 1.28396 17.3736 2.02234 19.2736 3.42033C20.189 4.09359 20.9873 4.89867 21.6484 5.8135C22.317 6.74042 22.8285 7.75871 23.1677 8.83845C23.1698 8.84527 23.1719 8.85158 23.1735 8.85788C22.5859 9.28011 21.8974 9.55635 21.1506 9.67451C21.3123 10.07 21.431 10.478 21.5061 10.8913C21.5077 10.8981 21.5082 10.9044 21.5098 10.9113C22.3947 10.7495 23.2287 10.4061 23.9565 9.87985C24.654 9.37359 24.519 8.80169 24.4129 8.46243C22.8757 3.56737 18.2265 0.0078125 12.7291 0.0078125C5.97807 0.0078125 0.505859 5.37604 0.505859 11.9984C0.505859 18.6207 5.97859 23.9889 12.7291 23.9889C18.2265 23.9889 22.8757 20.4288 24.4129 15.5311C24.5195 15.194 24.655 14.6226 23.9565 14.1158Z"
        fill="#3664AE"
      />
      <path
        d="M21.1514 9.67689C20.8767 9.72047 20.5952 9.74306 20.3064 9.74306C18.4531 9.74306 17.7567 9.13807 16.9511 8.4375C16.1744 7.76214 15.2081 6.92188 13.4478 6.92188H12.3969C11.125 6.92188 9.96802 7.37457 9.14036 8.19697C8.33213 9.00047 7.88574 10.0991 7.88574 11.2923V12.7034C7.88574 13.8966 8.33108 14.9963 9.14036 15.7987C9.96854 16.6211 11.125 17.0738 12.3969 17.0738H13.4478C15.2076 17.0738 16.1744 16.233 16.9511 15.5582C17.7567 14.8576 18.4526 14.2526 20.3064 14.2526C20.5942 14.2526 20.8767 14.2752 21.1514 14.3188C21.3131 13.9239 21.4308 13.5164 21.5069 13.1036C21.5085 13.0968 21.509 13.0899 21.5106 13.0831C21.1178 13.0117 20.715 12.9749 20.3069 12.9749C16.0478 12.9749 16.2695 15.7961 13.4488 15.7961H12.398C10.4622 15.7961 9.18973 14.4396 9.18973 12.7034V11.2923C9.18973 9.5561 10.4617 8.1996 12.398 8.1996H13.4488C16.2695 8.1996 16.0484 11.0208 20.3069 11.0208C20.715 11.0208 21.1183 10.984 21.5106 10.9126C21.509 10.9058 21.5085 10.8995 21.5069 10.8926C21.4318 10.4804 21.3131 10.0729 21.1514 9.67689Z"
        fill="#5FC2D6"
      />
      <path
        d="M21.1514 9.67689C20.8767 9.72047 20.5952 9.74306 20.3064 9.74306C18.4531 9.74306 17.7567 9.13807 16.9511 8.4375C16.1744 7.76214 15.2081 6.92188 13.4478 6.92188H12.3969C11.125 6.92188 9.96802 7.37457 9.14036 8.19697C8.33213 9.00047 7.88574 10.0991 7.88574 11.2923V12.7034C7.88574 13.8966 8.33108 14.9963 9.14036 15.7987C9.96854 16.6211 11.125 17.0738 12.3969 17.0738H13.4478C15.2076 17.0738 16.1744 16.233 16.9511 15.5582C17.7567 14.8576 18.4526 14.2526 20.3064 14.2526C20.5942 14.2526 20.8767 14.2752 21.1514 14.3188C21.3131 13.9239 21.4308 13.5164 21.5069 13.1036C21.5085 13.0968 21.509 13.0899 21.5106 13.0831C21.1178 13.0117 20.715 12.9749 20.3069 12.9749C16.0478 12.9749 16.2695 15.7961 13.4488 15.7961H12.398C10.4622 15.7961 9.18973 14.4396 9.18973 12.7034V11.2923C9.18973 9.5561 10.4617 8.1996 12.398 8.1996H13.4488C16.2695 8.1996 16.0484 11.0208 20.3069 11.0208C20.715 11.0208 21.1183 10.984 21.5106 10.9126C21.509 10.9058 21.5085 10.8995 21.5069 10.8926C21.4318 10.4804 21.3131 10.0729 21.1514 9.67689Z"
        fill="#5FC2D6"
      />
    </g>
    <defs>
      <clipPath id="clip0_206_1531">
        <rect width={24} height={24} fill="white" transform="translate(0.5)" />
      </clipPath>
    </defs>
  </svg>
);

export const ThreeDotLoader = ({ color = "#fb5f66", dimOpacity = 0.2 }) => {
  const loaderStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "5px",
  };

  const dotStyle: React.CSSProperties = {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: color,
    animation: `three-dot-bounce 1s infinite ease-in-out`,
  };

  const beforeAfterStyles = `
    @keyframes three-dot-bounce {
      0%, 80%, 100% {
        transform: scale(0.2);
        background-color: ${color}${Math.round(dimOpacity * 255)
          .toString(16)
          .padStart(2, "0")};
      }
      40% {
        transform: scale(1);
        background-color: ${color};
      }
    }
  `;

  return (
    <div style={loaderStyle} className="cbb-three-dot-loader">
      <style>{beforeAfterStyles}</style>
      <div
        style={{ ...dotStyle, animationDelay: "0s" }}
        className="cbb-loader-dot"
      ></div>
      <div
        style={{ ...dotStyle, animationDelay: "0.2s" }}
        className="cbb-loader-dot"
      ></div>
      <div
        style={{ ...dotStyle, animationDelay: "0.4s" }}
        className="cbb-loader-dot"
      ></div>
    </div>
  );
};

export const Fallbackpreview = (props) => (
  <svg
    width={76}
    height={50}
    viewBox="0 0 76 50"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <rect y={0.546875} width={76} height={49} rx={3} fill="#C7C7C7" />
    <g clipPath="url(#clip0_7355_98632)">
      <path
        d="M32.6674 35.7116C33.0224 35.7035 33.3755 35.7666 33.7062 35.8966C34.0368 36.0273 34.3374 36.2223 34.5918 36.4704C34.8462 36.7185 35.0474 37.0154 35.1855 37.3429C35.3237 37.6704 35.3943 38.0216 35.3943 38.3773C35.3943 38.7323 35.3237 39.0841 35.1855 39.4116C35.048 39.7391 34.8462 40.036 34.5924 40.2841C34.3387 40.5329 34.0374 40.7279 33.7074 40.8585C33.3768 40.9891 33.0237 41.0523 32.6687 41.0441C31.9718 41.0285 31.3087 40.741 30.8212 40.2429C30.3337 39.7448 30.0605 39.0754 30.0605 38.3785C30.0605 37.6816 30.333 37.0123 30.8199 36.5141C31.3074 36.0148 31.9705 35.7273 32.6674 35.7116Z"
        fill="#737373"
      />
      <path
        d="M43.2299 23.1327L39.6855 17.0045C39.338 16.3902 38.7011 16.0195 37.9949 16.0195C37.2874 16.0195 36.6505 16.3902 36.303 17.0045L32.7592 23.1439C32.4117 23.7577 32.4117 24.4989 32.7705 25.1127C33.118 25.7158 33.743 26.0745 34.4492 26.0745H41.528C42.2342 26.0745 42.8711 25.7039 43.2299 25.0895C43.5899 24.4758 43.5899 23.7458 43.2299 23.1327ZM37.9936 24.5158C37.6455 24.5158 37.3605 24.2314 37.3605 23.8827C37.3605 23.5339 37.6455 23.2508 37.9936 23.2508C38.3424 23.2508 38.6274 23.5339 38.613 23.8952C38.6274 24.2183 38.3292 24.5158 37.9936 24.5158ZM38.588 20.4345C38.5624 20.9252 38.5367 21.4027 38.4974 21.8933C38.4849 22.0489 38.4849 22.1908 38.4849 22.3464C38.4717 22.6045 38.2649 22.7977 38.0067 22.7977C37.748 22.7977 37.5549 22.617 37.528 22.3589C37.4774 21.6227 37.438 20.8733 37.3999 20.1114C37.3867 19.9052 37.3742 19.7108 37.3605 19.5045C37.3605 19.1814 37.5424 18.897 37.8511 18.8202C37.903 18.807 37.9555 18.8002 38.0067 18.8002C38.2511 18.8002 38.4817 18.9464 38.588 19.1814C38.6274 19.2845 38.6399 19.3877 38.6399 19.492C38.6274 19.8014 38.6005 20.1239 38.588 20.4345Z"
        fill="#737373"
      />
      <path
        d="M47.3331 9.04688H28.6669C24.9906 9.04688 22 12.0375 22 15.7137V26.38C22 30.0562 24.9906 33.0469 28.6669 33.0469H47.3331C51.0094 33.0469 54 30.0562 54 26.38V15.7137C54 12.0375 51.0094 9.04688 47.3331 9.04688ZM44.8481 26.0356C44.1481 27.2344 42.9069 27.9494 41.5281 27.9494H34.4494C33.0688 27.9494 31.835 27.2406 31.1488 26.0531C30.8088 25.4694 30.6281 24.8069 30.625 24.1362C30.6225 23.4681 30.7956 22.8056 31.1275 22.22L31.1313 22.2131L31.135 22.2063L34.6763 16.0731C35.01 15.4862 35.4919 14.9981 36.0706 14.6613C36.6512 14.3231 37.3169 14.1444 37.995 14.1444C38.6731 14.1444 39.3381 14.3231 39.9188 14.6613C40.4975 14.9981 40.9794 15.4862 41.3125 16.0731L44.8506 22.1894C45.1937 22.7769 45.375 23.4406 45.375 24.1106C45.375 24.7819 45.1931 25.4475 44.8481 26.0356Z"
        fill="#737373"
      />
      <path
        d="M53.9996 38.3806C53.9996 38.0269 53.859 37.6881 53.609 37.4375C53.359 37.1875 53.0196 37.0469 52.6659 37.0469H39.3328C38.979 37.0469 38.6403 37.1875 38.3896 37.4375C38.1396 37.6875 37.999 38.0269 37.999 38.3806C37.999 38.7344 38.1396 39.0731 38.3896 39.3231C38.6396 39.5731 38.979 39.7138 39.3328 39.7138H52.6659C53.0196 39.7138 53.3584 39.5731 53.609 39.3231C53.859 39.0738 53.9996 38.7344 53.9996 38.3806Z"
        fill="#737373"
      />
      <path
        d="M27.3331 38.3806C27.3331 38.0269 27.1925 37.6881 26.9425 37.4375C26.6925 37.1875 26.3531 37.0469 26 37.0469H23.3331C22.9794 37.0469 22.6406 37.1875 22.3906 37.4375C22.1406 37.6875 22 38.0269 22 38.3806C22 38.7344 22.1406 39.0731 22.3906 39.3231C22.6406 39.5731 22.98 39.7138 23.3331 39.7138H26C26.3538 39.7138 26.6925 39.5731 26.9425 39.3231C27.1931 39.0738 27.3331 38.7344 27.3331 38.3806Z"
        fill="#737373"
      />
      <path
        d="M43.2299 23.1327L39.6855 17.0045C39.338 16.3902 38.7011 16.0195 37.9949 16.0195C37.2874 16.0195 36.6505 16.3902 36.303 17.0045L32.7592 23.1439C32.4117 23.7577 32.4117 24.4989 32.7705 25.1127C33.118 25.7158 33.743 26.0745 34.4492 26.0745H41.528C42.2342 26.0745 42.8711 25.7039 43.2299 25.0895C43.5899 24.4758 43.5899 23.7458 43.2299 23.1327ZM37.9936 24.5158C37.6455 24.5158 37.3605 24.2314 37.3605 23.8827C37.3605 23.5339 37.6455 23.2508 37.9936 23.2508C38.3424 23.2508 38.6274 23.5339 38.613 23.8952C38.6274 24.2183 38.3292 24.5158 37.9936 24.5158ZM38.588 20.4345C38.5624 20.9252 38.5367 21.4027 38.4974 21.8933C38.4849 22.0489 38.4849 22.1908 38.4849 22.3464C38.4717 22.6045 38.2649 22.7977 38.0067 22.7977C37.748 22.7977 37.5549 22.617 37.528 22.3589C37.4774 21.6227 37.438 20.8733 37.3999 20.1114C37.3867 19.9052 37.3742 19.7108 37.3605 19.5045C37.3605 19.1814 37.5424 18.897 37.8511 18.8202C37.903 18.807 37.9555 18.8002 38.0067 18.8002C38.2511 18.8002 38.4817 18.9464 38.588 19.1814C38.6274 19.2845 38.6399 19.3877 38.6399 19.492C38.6274 19.8014 38.6005 20.1239 38.588 20.4345Z"
        fill="#737373"
      />
    </g>
    <defs>
      <clipPath id="clip0_7355_98632">
        <rect
          width={32}
          height={31.9994}
          fill="white"
          transform="translate(22 9.04688)"
        />
      </clipPath>
    </defs>
  </svg>
);

function ImageWithFallback({ src, alt, className }) {
  const [error, setError] = useState(false);

  if (error || !src) {
    return <Fallbackpreview className={className} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setError(true)}
    />
  );
}

const NavigationArrow = ({ rotate }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={6}
    height={10}
    viewBox="0 0 6 10"
    fill="none"
    style={{ transform: `rotate(${rotate}deg)` }}
  >
    <path
      d="M0.626626 3.48594L3.90488 0.207689C4.0387 0.0746653 4.21972 0 4.4084 0C4.59709 0 4.77811 0.0746653 4.91192 0.207689C4.97887 0.274084 5.032 0.353078 5.06826 0.440111C5.10452 0.527145 5.12319 0.620497 5.12319 0.714782C5.12319 0.809068 5.10452 0.90242 5.06826 0.989454C5.032 1.07649 4.97887 1.15548 4.91192 1.22188L1.62653 4.49299C1.55959 4.55938 1.50645 4.63838 1.47019 4.72541C1.43393 4.81244 1.41526 4.9058 1.41526 5.00008C1.41526 5.09437 1.43393 5.18772 1.47019 5.27475C1.50645 5.36179 1.55959 5.44078 1.62653 5.50717L4.91192 8.77829C5.04641 8.91183 5.12235 9.09333 5.12302 9.28285C5.12369 9.47238 5.04904 9.65441 4.91549 9.7889C4.78195 9.92339 4.60045 9.99932 4.41093 9.99999C4.2214 10.0007 4.03937 9.92602 3.90488 9.79247L0.626626 6.51422C0.225378 6.11247 0 5.56789 0 5.00008C0 4.43228 0.225378 3.88769 0.626626 3.48594Z"
      fill="#666666"
    />
  </svg>
);
